import type { PageContent, NameStyle } from '../types';

export interface IndustryLandingConfig {
  key: string;
  legacyKeys?: string[];
  navLabel: string;
  category: 'Fashion & Apparel' | 'Beauty & Cosmetics' | 'Luxury, Jewelry & Lifestyle' | 'Food, Beverage & Spirits' | 'Fitness, Health & Wellness' | 'Tech, Auto & Commerce';
  targetKeyword: string;
  slug: string;
  h1: string;
  subtitle: string;
  defaultIndustryInput: string;
  defaultKeywordsInput: string;
  defaultStyle: NameStyle;
  seoTitle: string;
  seoDescription: string;
  phoneticInsight: string;
  marketPositioning: string;
  frameworks: {
    name: string;
    explanation: string;
    examples: string[];
  }[];
  curatedCategories: {
    archetype: string;
    description: string;
    names: string[];
  }[];
  auditCriteria: {
    dimension: string;
    detail: string;
  }[];
  faqs: {
    question: string;
    answer: string;
  }[];
}

export const INDUSTRY_LANDING_CONFIGS: IndustryLandingConfig[] = [
  // ==========================================
  // 1. SHOE BRAND NAME GENERATOR (Improved)
  // ==========================================
  {
    key: 'shoeBrand',
    legacyKeys: ['shoe-brand-name-generator'],
    navLabel: 'Shoe Brand Names',
    category: 'Fashion & Apparel',
    targetKeyword: 'shoe brand name generator',
    slug: '/shoe-brand-name-generator/',
    h1: 'Shoe Brand Name Generator',
    subtitle: 'Generate memorable, high-velocity footwear and sneaker brand names engineered for phonetic punch, box-label aesthetics, and global trademark readiness.',
    defaultIndustryInput: 'Shoe',
    defaultKeywordsInput: 'stride, sole, velocity, craft',
    defaultStyle: 'Modern',
    seoTitle: 'Shoe Brand Name Generator: 500+ Footwear & Sneaker Ideas | BrandGenius',
    seoDescription: 'Use our free Shoe Brand Name Generator to create iconic footwear, sneaker, and luxury shoe brand names. Check domain availability and linguistic scores instantly.',
    phoneticInsight: 'In the global footwear market, kinetic phonetics dominate. Iconic shoe brands rely on short, 1-to-2 syllable structures featuring plosive consonants (K, P, T, B) or swift fricatives (S, V, Z) that subconsciously convey forward motion, traction, and agility.',
    marketPositioning: 'Whether you are launching a direct-to-consumer performance running shoe, a small-batch Italian leather dress shoe atelier, or a limited-drop sneaker label, your footwear brand name must perform in two critical environments: stamped in 8-point type on a heel tab or insole, and spoken effortlessly in word-of-mouth sneaker culture.',
    frameworks: [
      {
        name: '01. Kinetic & Motion-Driven Names',
        explanation: 'Rooted in biomechanics, speed, and momentum. Ideal for athletic footwear, marathon runners, and court shoes.',
        examples: ['VeloStride', 'ApexTread', 'Kinetix', 'PaceCore', 'AeroStep', 'SprintVibe']
      },
      {
        name: '02. Artisanal & Cobbler Heritage Names',
        explanation: 'Evokes Old-World craftsmanship, full-grain leather, Goodyear welt construction, and timeless sartorial authority.',
        examples: ['Maison Vane', 'Cobbled & Co.', 'Veritas Last', 'Sovereign Sole', 'Atelier Corso', 'Bespoke Stride']
      },
      {
        name: '03. Minimalist DTC & Sustainable Footwear',
        explanation: 'Clean, vowel-forward, nature-inspired names that communicate comfort, recycled materials, and everyday versatility.',
        examples: ['LumaStep', 'TerraSole', 'CloudLoom', 'PureTread', 'Solara Walk', 'EcoArch']
      },
      {
        name: '04. Street & Sneakerhead Culture Names',
        explanation: 'Abstract, high-contrast compound words and cultural signifiers built for hype drops and collector communities.',
        examples: ['VaultKicks', 'NoirTread', 'ConcreteArch', 'Syndicate Sole', 'Archive 09', 'DropVex']
      },
      {
        name: '05. Short Coined Neologisms',
        explanation: 'Invented 4-to-6 letter words with zero prior dictionary baggage—maximizing USPTO Class 25 trademark availability.',
        examples: ['Zovex', 'Velon', 'Stryx', 'Kovra', 'Tredix', 'Luvon']
      }
    ],
    curatedCategories: [
      {
        archetype: 'Performance & Athletic Sneaker Names',
        description: 'Engineered for high-output running, cross-training, and court sports.',
        names: ['VeloPulse', 'TrackApex', 'HyperStride', 'KineticSole', 'VoltTread', 'AeroSprint', 'PaceForge', 'VertexRun', 'PulseKicks', 'NitroArch', 'FlexMotion', 'ZenithPace']
      },
      {
        archetype: 'Luxury & Designer Heel / Dress Shoe Names',
        description: 'Sophisticated, editorial names for upscale heels, loafers, and boots.',
        names: ['Maison Éclat', 'VelvetStep', 'Aurelia Sole', 'Vanguard Heel', 'Lumiere Last', 'Sovereign Walk', 'NobleCobble', 'Orchid Stride', 'Palazzo Footwear', 'GildedArch', 'Verona Tread', 'Crest & Heel']
      },
      {
        archetype: 'Sustainable & Everyday Comfort Shoe Names',
        description: 'Warm, approachable names for anatomical, barefoot, and eco-friendly footwear.',
        names: ['CloudPath', 'TerraStep', 'RootedSole', 'CanopyWalk', 'BreezeTread', 'PureStride', 'HavenFootwear', 'OasisArch', 'FeatherSole', 'VerdantStep', 'SolaceWalk', 'BiomeKicks']
      },
      {
        archetype: 'Streetwear & Skate Footwear Names',
        description: 'Gritty, culture-first names for vulcanized skate shoes and urban silhouettes.',
        names: ['CurbVault', 'AsphaltLace', 'GrindCore', 'DistrictKicks', 'RawTread', 'ConcreteSole', 'VandalStep', 'MetroKicks', 'PhantomLace', 'SubwayStride', 'RiotSole', 'monolith Kicks']
      }
    ],
    auditCriteria: [
      { dimension: 'Tongue & Heel Tab Legibility', detail: 'Shoe names must fit cleanly inside a 35mm woven tongue label or debossed leather heel counter without visual crowding.' },
      { dimension: 'Class 25 Trademark Clearance', detail: 'Footwear falls under Nice Classification Class 25 (Apparel & Footwear), one of the most crowded registries globally. Coined or compound names dramatically reduce rejection risk.' },
      { dimension: 'Phonetic Velocity', detail: 'Plosive and fricative initials (Nike, Puma, Vans, Hoka, On) outperform multi-syllable descriptive phrases by 3.4x in aided brand recall.' }
    ],
    faqs: [
      {
        question: 'How does the Shoe Brand Name Generator create unique footwear names?',
        answer: 'Our deterministic naming engine combines footwear-specific root morphemes (such as stride, sole, tread, last, and arch) with stylistic modifiers and phonetic blending rules. It filters candidates by syllable count, visual symmetry, and pronounceability so every generated shoe brand name sounds like an established label.'
      },
      {
        question: 'What makes a shoe or sneaker brand name memorable?',
        answer: 'The most iconic shoe brands share three linguistic traits: brevity (1 to 2 syllables), kinetic sound symbolism (sharp consonants like K, V, Z, or P that suggest movement), and high visual symmetry for logo marks on shoe boxes and tongues.'
      },
      {
        question: 'Should I include the word "Shoes" or "Footwear" in my brand name?',
        answer: 'Create a distinct core brand name first (e.g., "Veloce" or "CloudTread") and use "Footwear" or "Shoes" only as a secondary descriptor for SEO and domain acquisition (such as CloudTreadFootwear.com) if the exact-match .com is unavailable. This allows your brand to expand into apparel and accessories later.'
      },
      {
        question: 'How do I check if my shoe brand name is available to register?',
        answer: 'Every name generated on this page includes a one-click Domain Availability check to verify .com registration status, and you can run your top contenders through our free Brand Audit tool to evaluate phonetic clarity and digital readiness.'
      }
    ]
  },

  // ==========================================
  // 2. COFFEE BRAND NAME GENERATOR (Improved)
  // ==========================================
  {
    key: 'coffeeBrand',
    legacyKeys: ['coffee-brand-name-generator'],
    navLabel: 'Coffee Brand Names',
    category: 'Food, Beverage & Spirits',
    targetKeyword: 'coffee brand name generator',
    slug: '/coffee-brand-name-generator/',
    h1: 'Coffee Brand Name Generator',
    subtitle: 'Craft rich, sensory, and unforgettable names for specialty coffee roasters, third-wave cafés, cold brew RTD startups, and espresso equipment brands.',
    defaultIndustryInput: 'Coffee',
    defaultKeywordsInput: 'roast, ritual, crema, origin',
    defaultStyle: 'Creative',
    seoTitle: 'Coffee Brand Name Generator: 500+ Café & Roastery Ideas | BrandGenius',
    seoDescription: 'Generate warm, memorable coffee brand names for specialty roasters, coffee shops, and cold brew brands. Instant domain checks and naming strategy guide.',
    phoneticInsight: 'Sensory linguistics in specialty coffee favors warm, resonant back vowels (O, U, A) paired with crisp stops (C, K, B, R) that mirror the sensory contrast between velvety crema and bright acidity.',
    marketPositioning: 'In the booming DTC coffee and third-wave café market, consumers buy a daily ritual and an origin story—not just caffeine. Your coffee brand name must command shelf presence on a 12oz matte-finish gusset bag while feeling welcoming on a storefront blade sign.',
    frameworks: [
      {
        name: '01. Origin & Terroir-Inspired Names',
        explanation: 'Highlights altitude, volcanic soil, harvest traditions, and geographical prestige for single-origin roasters.',
        examples: ['Highland Crema', 'Solstice Roast', 'Altitude 1800', 'TerraBean', 'Meridian Roasters', 'Canopy & Cup']
      },
      {
        name: '02. Ritual & Morning Momentum Names',
        explanation: 'Focuses on daily mindfulness, creative energy, and the sacred first pour of the morning.',
        examples: ['FirstLight Brew', 'Ritualist Coffee', 'Awaken Lab', 'Kindred Pour', 'Daybreak Crema', 'Stillness Roast']
      },
      {
        name: '03. Modern Cold Brew & RTD Functional Coffee',
        explanation: 'Punchy, high-energy names built for aluminum cans, Whole Foods endcaps, and nootropic coffee blends.',
        examples: ['JoltCraft', 'NitroPulse', 'VeloBrew', 'HyperBean', 'FluxCoffee', 'OptiRoast']
      },
      {
        name: '04. Artisanal Guild & Roastery Names',
        explanation: 'Evokes cast-iron drum roasters, small-batch precision, and craft culinary heritage.',
        examples: ['Ember & Drum', 'Ironclad Roasters', 'CopperPot Coffee', 'Foundry Brew', 'Alchemist Bean', 'Hearthstone Roast']
      },
      {
        name: '05. Neighborhood Café & Community Hub Names',
        explanation: 'Warm, conversational names that invite remote workers, creatives, and locals to linger.',
        examples: ['Velvet Mug', 'The Daily Grindhouse', 'CommonGrounds', 'Bramble & Brew', 'Sunlit Espresso', 'Harbor Café']
      }
    ],
    curatedCategories: [
      {
        archetype: 'Third-Wave Specialty Roastery Names',
        description: 'Sophisticated, minimalist names for micro-lot and direct-trade coffee roasters.',
        names: ['Lumen Roasters', 'Aether Coffee Co.', 'Epoch Espresso', 'Monolith Brew', 'Kinetix Roast', 'Vanguard Bean', 'Solara Roastery', 'Prism Coffee Lab', 'Origin & Co.', 'Veritas Roast', 'Nomad Crema', 'Zenith Roasters']
      },
      {
        archetype: 'Cozy Café & Espresso Bar Names',
        description: 'Inviting, character-rich names for brick-and-mortar coffee shops and bakeries.',
        names: ['Hearth & Crema', 'Morning Glory Café', 'Copper Spoon Espresso', 'Willow & Bean', 'The Roasted Finch', 'Amber Cup', 'Velvet Steam', 'Cobblestone Café', 'Fable Espresso', 'Lantern Coffee House', 'Kindred Mug', 'Honeycomb Brew']
      },
      {
        archetype: 'Canned Cold Brew & Energy Coffee Names',
        description: 'Bold, high-impact DTC brand names for ready-to-drink cold brew and functional mushroom/nootropic coffees.',
        names: ['VoltBrew', 'ApexCold', 'JoltFuel', 'NovaRoast', 'PulseEspresso', 'IgniteBean', 'SurgeCoffee', 'CoreBrew', 'ShiftRoast', 'KineticCup', 'TitanBrew', 'ZestRoast']
      },
      {
        archetype: 'Luxury & Italian-Style Espresso Names',
        description: 'European-inspired names conveying dark-roast mastery and heritage.',
        names: ['Caffè Sovrano', 'Veloce Espresso', 'Maison Crema', 'Oro Nero Roast', 'Palazzo Bean', 'Torino Pour', 'Lusso Coffee', 'Bellissimo Brew', 'Cortado Club', 'Barista Nobile', 'Verona Crema', 'Ristretto Royale']
      }
    ],
    auditCriteria: [
      { dimension: 'Packaging & Bag Typography', detail: 'Coffee bags are viewed from 4–6 feet away on retail shelves. Names with 5–9 letters allow large, high-contrast serif or grotesque typography.' },
      { dimension: 'Sensory Phonetics', detail: 'Names containing bilabial and velar consonants (B, M, C, K, G) trigger stronger gustatory associations with richness and aroma.' },
      { dimension: 'DTC Subscription Recall', detail: 'Recurring coffee subscriptions rely on direct navigation traffic—avoiding hyphens or complex foreign spellings prevents customer leakage.' }
    ],
    faqs: [
      {
        question: 'How do I choose a name for my coffee brand or café?',
        answer: 'Start by defining your primary channel: retail/DTC coffee bags require short, punchy, trademarkable names (like Blue Bottle, Stumptown, or Chamberlain), whereas local cafés benefit from warm, atmospheric names that evoke community and ritual.'
      },
      {
        question: 'What words are best to use in a coffee brand name?',
        answer: 'High-converting coffee root words include sensory and craft terms like Roast, Crema, Pour, Ritual, Ember, Origin, Bloom, Grind, Vessel, and Canopy. Combining one coffee-adjacent word with an unexpected evocative noun creates instant memorability.'
      },
      {
        question: 'Can I trademark a coffee brand name generated here?',
        answer: 'Yes! Because our generator builds distinctive compound words and coined blends rather than generic geographic descriptions, your generated candidates have a much stronger foundation for USPTO Class 30 (Coffee & Tea) and Class 43 (Café Services) registration.'
      },
      {
        question: 'How do I test if my coffee brand name is strong?',
        answer: 'After generating a shortlist above, click to verify .com domain availability and run your favorite name through the BrandGenius Brand Audit Engine to score its phonetic clarity, memorability, and digital readiness.'
      }
    ]
  },

  // ==========================================
  // 3. STREETWEAR BRAND NAME GENERATOR (Improved)
  // ==========================================
  {
    key: 'streetwearBrand',
    legacyKeys: ['streetwear-brand-name-generator'],
    navLabel: 'Streetwear Brand Names',
    category: 'Fashion & Apparel',
    targetKeyword: 'streetwear brand name generator',
    slug: '/streetwear-brand-name-generator/',
    h1: 'Streetwear Brand Name Generator',
    subtitle: 'Forge culturally resonant, underground, and luxury streetwear brand names engineered for heavyweight hoodies, woven neck labels, and global hype drops.',
    defaultIndustryInput: 'Streetwear',
    defaultKeywordsInput: 'urban, archive, noir, syndicate',
    defaultStyle: 'Modern',
    seoTitle: 'Streetwear Brand Name Generator: 500+ Urban Clothing Ideas | BrandGenius',
    seoDescription: 'Generate authentic streetwear brand names for urban apparel, skate labels, and luxury street fashion. Instant domain checks & naming playbook.',
    phoneticInsight: 'Streetwear naming subverts traditional corporate branding. It thrives on stark juxtaposition, monolithic nouns (Supreme, Palace, Stüssy, Kith), dystopian or architectural vocabulary, and sharp consonant clusters that look iconic in bold Helvetica or Gothic blackletter.',
    marketPositioning: 'In streetwear, the brand name IS the graphic. Unlike traditional apparel where the logo is hidden inside the collar, a streetwear name is screen-printed across chests, embroidered on 5-panel caps, and traded on secondary markets. It must carry cultural weight, scarcity appeal, and visual authority.',
    frameworks: [
      {
        name: '01. Monolithic Power Nouns',
        explanation: 'Single, commanding dictionary words recontextualized with irony, authority, or counter-cultural swagger.',
        examples: ['Sovereign', 'Citadel', 'Monolith', 'Syndicate', 'Vanguard', 'Phantom']
      },
      {
        name: '02. Archive, Studio & Collective Format',
        explanation: 'Positions the clothing line as a multidisciplinary design house, underground guild, or curated archive.',
        examples: ['Noir Archive', 'Concrete Collective', 'District 04 Studio', 'Vault Syndicate', 'Heavyweight Dept.', 'Subculture Lab']
      },
      {
        name: '03. Juxtaposed & Paradoxical Compounds',
        explanation: 'Pairs two contrasting words—one delicate/elevated and one gritty/industrial—to create narrative tension.',
        examples: ['Velvet Concrete', 'Silent Riot', 'Gilded Asphalt', 'Saint Vandal', 'Pure Static', 'Sacred Gutter']
      },
      {
        name: '04. Luxury High-Street & Atelier Fusion',
        explanation: 'Blends European couture aesthetics with heavyweight skate and hip-hop roots.',
        examples: ['Maison Vex', 'Atelier Kicks', 'Noir Éclat', 'Veritas Street', 'Château Raw', 'Luxe Brutal']
      },
      {
        name: '05. Coded Numerics & Dystopian Techwear',
        explanation: 'Futuristic, tactical, and cyber-industrial nomenclature ideal for gore-tex shells, cargo pants, and techwear.',
        examples: ['Sector Nine', 'Kinetix 99', 'Zero Protocol', 'Vector Void', 'CyberLoom', 'Nexus Shell']
      }
    ],
    curatedCategories: [
      {
        archetype: 'Underground Skate & Graphic Streetwear Names',
        description: 'Raw, rebellious names built for screen-printed tees, skate decks, and hoodies.',
        names: ['Asphalt Syndicate', 'Curb Archive', 'Static Club', 'Vandal Dept.', 'Grind Division', 'Deadstock Society', 'Concrete Youth', 'Raw District', 'Phantom Thread', 'Subway Guild', 'Riot Canvas', 'Outcast Label']
      },
      {
        archetype: 'Luxury & Elevated Streetwear Names',
        description: 'High-margin, runway-ready streetwear names for heavyweight knitwear and tailored street silhouettes.',
        names: ['Maison Noir', 'Sovereign Atelier', 'Velvet Brut', 'Aether Garments', 'Obsidian Studio', 'Crown & Concrete', 'Valour Archive', 'Lumiere Street', 'Vanguard Dept.', 'Veritas Heavy', 'Palazzo Raw', 'Eclipse Collective']
      },
      {
        archetype: 'Techwear & Tactical Urban Apparel Names',
        description: 'Precision, utilitarian names for modular outerwear and futuristic urban clothing.',
        names: ['Vexel Systems', 'ChronoShell', 'Axis Protocol', 'KiloWeave', 'Vertex Tactical', 'OmniThread', 'CyberDraft', 'PulseDivision', 'ZeroGrid Apparel', 'AeroVault', 'Matrix Garment', 'ShiftCore']
      },
      {
        archetype: 'One-Word Iconic Streetwear Names',
        description: 'Punchy, 4-to-7 letter coined and evocative single-word brand names.',
        names: ['Kovex', 'Vexor', 'Stryde', 'Zentho', 'Nuvon', 'Kryos', 'Veltro', 'Zenith', 'Noxen', 'Valex', 'Drako', 'Solvex']
      }
    ],
    auditCriteria: [
      { dimension: 'Box-Logo Visual Balance', detail: 'Streetwear names must have balanced kerning and character geometry (avoiding awkward ascender/descender gaps) when set inside a rectangular box logo or chest print.' },
      { dimension: 'Scarcity & Cultural Gravity', detail: 'Avoid overly literal clothing words like "Apparel" or "Fashions" in the core mark; use "Archive", "Dept.", "Club", "Studio", or stand-alone nouns instead.' },
      { dimension: 'Handle & Drop Domain Hygiene', detail: 'Drop-based streetwear brands rely heavily on Instagram and TikTok traffic—securing a clean @handle and .com domain is essential to prevent replica/scam impersonation.' }
    ],
    faqs: [
      {
        question: 'How do I come up with a hard streetwear brand name?',
        answer: 'The strongest streetwear names avoid sounding like traditional retail stores. Combine gritty urban or architectural imagery (Concrete, Vault, Archive, Asphalt, Sector) with elevated or philosophical nouns (Saint, Sovereign, Velvet, Veritas), or use our generator on Modern/Creative mode to coin a punchy 1-word handle.'
      },
      {
        question: 'What words make a streetwear brand sound expensive?',
        answer: 'Adding design-house signifiers such as "Atelier", "Archive", "Maison", "Departments", "Collective", or "Studios" elevates a streetwear brand from basic merch to cut-and-sew luxury streetwear.'
      },
      {
        question: 'Should my streetwear brand name be one word or two words?',
        answer: 'One-word names (like Kith, Palace, Supreme, Stüssy) are ideal for bold chest graphics and monogram logos, while two-word paradoxical names (like Off-White, Fear of God, Acne Studios) tell an immediate story and are often easier to trademark in Class 25.'
      },
      {
        question: 'How can I verify if my streetwear brand name is taken?',
        answer: 'Use the instant Domain Availability button next to each generated streetwear name above, and run your top choice through our Brand Audit & Instagram Audit tools to ensure your digital footprint is clear.'
      }
    ]
  },

  // ==========================================
  // 4. LUXURY BRAND NAME GENERATOR
  // ==========================================
  {
    key: 'luxuryBrand',
    legacyKeys: ['luxury-brand-name-generator'],
    navLabel: 'Luxury Brand Names',
    category: 'Luxury, Jewelry & Lifestyle',
    targetKeyword: 'luxury brand name generator',
    slug: '/luxury-brand-name-generator/',
    h1: 'Luxury Brand Name Generator',
    subtitle: 'Create prestigious, high-margin luxury brand names crafted with Old-World heritage, euphonic elegance, and timeless haute-couture authority.',
    defaultIndustryInput: 'Luxury',
    defaultKeywordsInput: 'atelier, sovereign, lumiere, prestige',
    defaultStyle: 'Classic',
    seoTitle: 'Luxury Brand Name Generator: Prestigious High-End Ideas | BrandGenius',
    seoDescription: 'Generate timeless, sophisticated luxury brand names for fashion houses, bespoke goods, and quiet-luxury startups. Free domain check & naming guide.',
    phoneticInsight: 'Luxury linguistics is governed by liquid consonants (L, R, M, N) and open, elongated vowels (A, E, O) drawn from Latin, French, and Italian roots. These "euphonic" sounds slow down speech cadence, forcing the speaker to savor the name and signaling rarity and poise.',
    marketPositioning: 'True luxury never shouts. Whether you are building a "quiet luxury" cashmere house, a bespoke leather goods maison, or an ultra-high-net-worth hospitality brand, your name must project permanence, lineage, and uncompromising scarcity.',
    frameworks: [
      {
        name: '01. The European Maison & Atelier Convention',
        explanation: 'Anchors the brand in Parisian or Milanese artisanal heritage using classic house prefixes.',
        examples: ['Maison Valour', 'Atelier Lumiere', 'Château Éclat', 'Casa Sovrana', 'Maison Aurelia', 'Atelier Vane']
      },
      {
        name: '02. Aristocratic & Dynastic Surnames',
        explanation: 'Evokes generational wealth, noble estates, and bespoke Savile Row or Place Vendôme founders.',
        examples: ['Vane & Sterling', 'Laurentius', 'Montclair & Co.', 'Beauregard', 'Castellano', 'Kensington Crest']
      },
      {
        name: '03. Classical Latin & Mythological Signifiers',
        explanation: 'Draws upon timeless concepts of truth, light, eternity, and gold.',
        examples: ['Veritas Aura', 'Aetheris', 'Solara Veritas', 'Aureum', 'Caelestis', 'Omnia Nobile']
      },
      {
        name: '04. Quiet Luxury Minimalist Monograms',
        explanation: 'Understated, symmetrical names designed to be foil-stamped in gold or blind-debossed on calfskin.',
        examples: ['Velora', 'Lumina', 'Sovran', 'Élanor', 'Valen', 'Orixa']
      },
      {
        name: '05. Bespoke Guild & Crest Titles',
        explanation: 'Ideal for private aviation, yachting, fine horology, and bespoke concierge brands.',
        examples: ['Sovereign Crest', 'Vanguard Royale', 'Pinnacle Reserve', 'Imperial Meridian', 'Noble Vault', 'Crown & Scepter']
      }
    ],
    curatedCategories: [
      {
        archetype: 'Haute Couture & Quiet Luxury Fashion Names',
        description: 'Poised, editorial names for silk, cashmere, and bespoke tailoring labels.',
        names: ['Maison Élan', 'Aurelia Atelier', 'Velvet Sovereign', 'Lumiere & Co.', 'Valour Silk', 'Seraphina Maison', 'Noir Prestige', 'Celeste Couture', 'Vanguard Linen', 'Éclat Royale', 'Verona Cashmere', 'Noble Silhouette']
      },
      {
        archetype: 'Ultra-Luxury Leather Goods & Accessories Names',
        description: 'Timeless names built for brass hardware engravings and dust bags.',
        names: ['Sovereign Hide', 'Atelier Corso', 'Gilded Crest', 'Montclair Leather', 'Veritas Vault', 'Crown & Clasp', 'Maison Veloce', 'Opulence Guild', 'Palazzo Saddlery', 'Sterling & Hide', 'Royal Tannery', 'Echelon Goods']
      },
      {
        archetype: 'Private Concierge, Yachting & Hospitality Names',
        description: 'Exclusive nomenclature for VIP membership clubs, boutique hotels, and charters.',
        names: ['Meridian Privé', 'Aether Sanctum', 'Solstice Royale', 'Zenith Reserve', 'Pinnacle Enclave', 'Sovereign Harbor', 'Aura Privée', 'Crestline Bespoke', 'Vanguard Society', 'Lumina Estates', 'Elysian Vault', 'Ascendia Club']
      },
      {
        archetype: 'Single-Word Coined Luxury Names',
        description: 'Fluid, melodious one-word names with high global trademark distinctiveness.',
        names: ['Veloria', 'Aurevex', 'Luminor', 'Sovrana', 'Eclatia', 'Valenx', 'Orelia', 'Nobilis', 'Celestia', 'Vespera', 'Aethera', 'Morvano']
      }
    ],
    auditCriteria: [
      { dimension: 'Foil-Stamp & Monogram Symmetry', detail: 'Luxury names must look impeccable when set in wide-tracked uppercase serif typography (such as Didot, Bodoni, or Cinzel) with balanced first and last letters.' },
      { dimension: 'Cross-Border Linguistic Purity', detail: 'High-luxury brands sell in Paris, Tokyo, Dubai, New York, and Milan—names must avoid awkward phonetic collisions in Romance and Asian languages.' },
      { dimension: 'Euphonic Cadence', detail: 'A high ratio of sonorants (L, M, N, R) and long vowels increases perceived price point by up to 28% in consumer psycholinguistic studies.' }
    ],
    faqs: [
      {
        question: 'What makes a brand name sound luxurious and expensive?',
        answer: 'Psycholinguistic research shows that luxury brand names rely on euphony—smooth liquid consonants (L, M, N, R), soft sibilants, and open vowels derived from French, Italian, or Latin roots. They also favor symmetry and heritage structures like founder surnames or "Maison / Atelier" pairings.'
      },
      {
        question: 'How do I use the Luxury Brand Name Generator effectively?',
        answer: 'Select the "Classic" or "Creative" style in the generator form above and enter evocative keywords such as "lumiere, sovereign, atelier, velvet, or crest." Our engine will synthesize prestigious name candidates and let you verify .com domain availability immediately.'
      },
      {
        question: 'Should a new luxury brand use a French or Italian name?',
        answer: 'Only if it aligns with your product craftsmanship or aesthetic DNA. Alternatively, Latin-rooted neologisms (like Veloria, Luminor, or Aetheris) convey universal European sophistication without claiming a specific geographic origin.'
      },
      {
        question: 'How do I validate my luxury brand name before investing in packaging?',
        answer: 'Run your top candidates through the BrandGenius Brand Audit Engine to score their Phonetic Clarity, Linguistic Efficiency, and Structural Integrity before commissioning custom die-cut packaging or trademark filings.'
      }
    ]
  },

  // ==========================================
  // 5. CAR BRAND NAME GENERATOR
  // ==========================================
  {
    key: 'carBrand',
    legacyKeys: ['car-brand-name-generator'],
    navLabel: 'Car & Auto Brand Names',
    category: 'Tech, Auto & Commerce',
    targetKeyword: 'car brand name generator',
    slug: '/car-brand-name-generator/',
    h1: 'Car Brand Name Generator',
    subtitle: 'Engineer aerodynamic, high-torque automotive brand names for EV startups, hypercar marques, custom tuning houses, and mobility innovators.',
    defaultIndustryInput: 'Automotive',
    defaultKeywordsInput: 'apex, veloce, volt, torque',
    defaultStyle: 'Modern',
    seoTitle: 'Car Brand Name Generator: 500+ Automotive & EV Name Ideas | BrandGenius',
    seoDescription: 'Create iconic car brand names for electric vehicles, supercars, auto detailing, and aftermarket parts. Instant domain checks & automotive naming guide.',
    phoneticInsight: 'Automotive nomenclature splits into two powerful sonic archetypes: aerodynamic Italian/Latin vowels (Ferrari, Maserati, Pagani, Alfa) that evoke sculptural curves and high-revving acoustics, and precision German/Tech consonants (Porsche, Audi, Lucid, Rivian, Tesla) that signal engineering mastery.',
    marketPositioning: 'From next-generation electric vehicle (EV) platforms and autonomous mobility startups to bespoke restomod builders and premium auto detailing brands, an automotive name must look iconic cast in chrome or illuminated on a front grille badge.',
    frameworks: [
      {
        name: '01. Next-Gen EV & Autonomous Mobility Names',
        explanation: 'Clean, luminous, forward-looking names that evoke silent torque, clean energy, and aerodynamic efficiency.',
        examples: ['LumaMotor', 'AetherDrive', 'VoltApex', 'Solara EV', 'Nimbus Mobility', 'Kinetix Auto']
      },
      {
        name: '02. Italianate Supercar & Grand Tourer Names',
        explanation: 'Sculptural, vowel-ending names inspired by famous racing circuits, mountain passes, and velocity.',
        examples: ['Veloce Strada', 'Corsa Nova', 'Valour GT', 'Torino Apex', 'Scuderia Vex', 'Avanti Motor']
      },
      {
        name: '03. Precision Engineering & Motorsport Tuning',
        explanation: 'Technical, metallic names for performance aftermarket parts, forged wheels, and tuning garages.',
        examples: ['Apex Autowerks', 'TorqueForge', 'Chassis Lab', 'CarbonVelo', 'Vector Dynamics', 'Ignition Core']
      },
      {
        name: '04. Rugged Off-Road & Overland Vehicle Names',
        explanation: 'Muscular, terrain-inspired names for 4x4 builds, overland trailers, and adventure trucks.',
        examples: ['TitanRidge', 'TerraTorque', 'Summit Axle', 'IronTrail Motors', 'Bedrock 4x4', 'Vanguard Overland']
      },
      {
        name: '05. Luxury Auto Detailing & Concierge Fleet Names',
        explanation: 'Prestigious names for exotic car rentals, ceramic coating studios, and collector vaults.',
        examples: ['Sovereign Motorclub', 'Concours Vault', 'Obsidian Auto Spa', 'Prestige Paddock', 'Maison Motoring', 'Apex Garage']
      }
    ],
    curatedCategories: [
      {
        archetype: 'Electric Vehicle (EV) & Clean Mobility Brand Names',
        description: 'Futuristic, high-trust names for electric cars, battery tech, and micromobility.',
        names: ['Voltara', 'AeroPulse', 'Lumina Motors', 'IonStride', 'Quantum Drive', 'HelioAuto', 'VeloVolt', 'Zenith Mobility', 'FluxMotors', 'NovaChassis', 'AetherEV', 'Optima Drive']
      },
      {
        archetype: 'Hypercar, Sports Car & Racing Marque Names',
        description: 'Aggressive, aerodynamic names built for speed and track heritage.',
        names: ['Veloce GT', 'Korsar', 'StradaX', 'Apexon', 'Valour Corsa', 'Vortex Motors', 'Hyperion Auto', 'Zentorno', 'Kryos GT', 'Vanguard Racing', 'Ignis Motorworks', 'ChronoDrive']
      },
      {
        archetype: 'Aftermarket Parts, Tuning & Custom Shop Names',
        description: 'Commanding names for performance exhausts, suspension, wheels, and restomods.',
        names: ['TorqueShift', 'ForgedApex', 'MotorCraft Lab', 'DynoCore', 'BoostFoundry', 'Redline Syndicate', 'CarbonPulse', 'GearVault', 'Piston & Co.', 'Veloce Customs', 'ShiftDynamics', 'TrackForge']
      },
      {
        archetype: 'Premium Auto Detailing & Exotic Dealership Names',
        description: 'Sleek, trustworthy names for ceramic coating studios, wraps, and luxury showrooms.',
        names: ['MirrorFinish Studio', 'Obsidian Detailing', 'Concours Atelier', 'LuxeMotor Vault', 'Paddock Prestige', 'GlossSyndicate', 'Sovereign Auto', 'VelvetChrome', 'EliteChassis', 'Marque & Co.', 'Pinnacle Motors', 'Aura Detailing']
      }
    ],
    auditCriteria: [
      { dimension: 'Grille & Steering Wheel Badge Geometry', detail: 'Automotive names must compress cleanly into a circular, shield, or horizontal wordmark on a steering wheel hub and rear decklid.' },
      { dimension: 'Global Trim-Level Scalability', detail: 'A great car brand name pairs effortlessly with alphanumeric model designations (e.g., Voltara GT, Voltara S, Voltara R-Spec).' },
      { dimension: 'Acoustic Authority', detail: 'High-impact consonants (V, K, T, X, Z) create a sense of mechanical precision and road grip.' }
    ],
    faqs: [
      {
        question: 'How were famous car brand names created?',
        answer: 'Historically, car brands used founder surnames (Ford, Ferrari, Porsche, Toyota, Tesla). Modern automotive disruptors (like Lucid, Rivian, Polestar, and Rimac) favor evocative English or coined words that symbolize light, direction, nature, and electrical energy.'
      },
      {
        question: 'What makes a great electric car (EV) brand name?',
        answer: 'Successful EV brand names avoid greasy mechanical clichés (like "Piston" or "Carburetor") and instead use roots associated with light, air, physics, and silent velocity—such as Lumen, Ion, Aero, Volt, Zenith, and Aether.'
      },
      {
        question: 'Can I use this generator for car detailing or auto parts brands?',
        answer: 'Absolutely. Simply enter keywords like "detail, gloss, ceramic, forge, or torque" into theKeywords field above to generate specialized names for auto detailing studios, aftermarket parts brands, or dealerships.'
      },
      {
        question: 'How do I check USPTO Class 12 suitability for my car brand name?',
        answer: 'Vehicles and automotive parts fall under International Class 12. Coined blends and distinctive compound names generated by BrandGenius offer significantly higher distinctiveness than generic geographic or descriptive terms.'
      }
    ]
  },

  // ==========================================
  // 6. SKINCARE BRAND NAME GENERATOR
  // ==========================================
  {
    key: 'skincareBrand',
    legacyKeys: ['skincare-brand-name-generator'],
    navLabel: 'Skincare Brand Names',
    category: 'Beauty & Cosmetics',
    targetKeyword: 'skincare brand name generator',
    slug: '/skincare-brand-name-generator/',
    h1: 'Skincare Brand Name Generator',
    subtitle: 'Formulate clean, clinical, and botanical skincare brand names engineered for frosted glass dropper bottles, dermatologist trust, and Sephora-ready shelf appeal.',
    defaultIndustryInput: 'Skincare',
    defaultKeywordsInput: 'glow, serum, cellular, dew',
    defaultStyle: 'Modern',
    seoTitle: 'Skincare Brand Name Generator: 500+ Clean & Clinical Ideas | BrandGenius',
    seoDescription: 'Generate dermatologist-trusted, botanical, and luxury skincare brand names instantly. Check .com domains and explore our skincare naming playbook.',
    phoneticInsight: 'Skincare naming relies on "clean phonetics"—high ratios of sibilants (S, C, Z), liquids (L, R), and bright front vowels (I, E) that subconsciously communicate purity, hydration, cellular renewal, and smoothness.',
    marketPositioning: 'In the hyper-competitive skincare industry, packaging real estate is tiny: a 30ml serum bottle has less than 4 centimeters of horizontal label width. Your skincare brand name must instantly signal your formulation philosophy—whether clinical bio-actives, barrier-repair dermatology, or dewy botanical rituals.',
    frameworks: [
      {
        name: '01. Clinical & Dermatological Bio-Science Names',
        explanation: 'Communicates peer-reviewed efficacy, peptides, retinoids, and lab-grade cellular repair.',
        examples: ['DermaCore Lab', 'CelluLuma', 'BioBarrier', 'Peptide Protocol', 'EpidermIQ', 'Clinical Dew']
      },
      {
        name: '02. Dewy, Glass-Skin & K-Beauty Inspired Names',
        explanation: 'Luminous, hydrated, soft-sounding names that promise radiance, bounce, and moisture.',
        examples: ['DewDrop Lab', 'GlowVessel', 'Lumina Skin', 'CloudSerum', 'GlassPetal', 'AuraDew']
      },
      {
        name: '03. Botanical, Earth-First & Clean Apothecary Names',
        explanation: 'Rooted in cold-pressed plant oils, adaptogens, marine algae, and holistic skin barrier health.',
        examples: ['Botanica Pure', 'Verdant Elixir', 'FloraBiome', 'Root & Dew', 'Canopy Skin', 'Solstice Botanicals']
      },
      {
        name: '04. Minimalist Everyday Routine & Gen-Z Skincare',
        explanation: 'Approachable, conversational, routine-focused names designed for viral TikTok shelfies.',
        examples: ['DailyLayer', 'BarelyThere Skin', 'GoodBarrier', 'SundaySerum', 'PureHabit', 'SkinSync']
      },
      {
        name: '05. Prestige Anti-Aging & Luxury Cellular Esthetics',
        explanation: 'High-price-point French and Swiss-inspired names for caviar creams, exosomes, and spa facials.',
        examples: ['Maison Lumière', 'Atelier Derma', 'Éclat Cellulaire', 'Sovereign Elixir', 'Orchidée Skin', 'Veritas Esthetics']
      }
    ],
    curatedCategories: [
      {
        archetype: 'Clinical & Dermatologist Skincare Brand Names',
        description: 'High-trust, scientific names for active serums, sunscreens, and barrier creams.',
        names: ['DermaLogic Lab', 'BiomeShield', 'CeraVex', 'CelluPure', 'ActivaSkin', 'OptiDerma', 'Lab 04 Skincare', 'MicroBiome Co.', 'Synthesis Skin', 'DermalPulse', 'Retinova', 'BarrierCore']
      },
      {
        archetype: 'Clean, Vegan & Botanical Skincare Names',
        description: 'Organic, soothing names for natural facial oils, cleansers, and botanical masks.',
        names: ['Petal & Dew', 'Verdant Glow', 'TerraBiome', 'AloeSanctuary', 'Botanic Vessel', 'PureNectar Skin', 'Meadow Elixir', 'Solara Botanicals', 'Willow & Serum', 'BloomBarrier', 'EdenDew', 'Herbarium Skin']
      },
      {
        archetype: 'Luminous & Dewy DTC Skincare Names',
        description: 'Modern, photogenic names built for social commerce and cult-favorite moisturizers.',
        names: ['GlowTheory', 'DewState', 'LumaDrop', 'SilkLayer', 'Radiant Lab', 'VelvetDew', 'CloudCream Co.', 'HaloSkin', 'PrismGlow', 'MirrorSkin', 'Plump & Pure', 'Sunlit Serum']
      },
      {
        archetype: 'Men’s Grooming & Performance Skincare Names',
        description: 'Crisp, utilitarian names for men’s facial cleansers,SPF moisturizers, and eye serums.',
        names: ['SlateSkin', 'ApexGroom', 'ForgeDerma', 'Kinetic Face', 'Elemental Men', 'Ironclad Skin', 'Monolith Grooming', 'Nordic Derma', 'Vanguard Skin', 'PrimeLayer', 'TrueGrind Skin', 'basalt Skincare']
      }
    ],
    auditCriteria: [
      { dimension: '30ml Dropper Bottle Legibility', detail: 'Skincare names over 12 characters wrap awkwardly around cylindrical 1oz serum bottles. Aim for 5–9 characters for maximum front-facing shelf impact.' },
      { dimension: 'FDA & Regulatory Claim Safety', detail: 'Avoid embedding medical cure claims (like "AcneCure" or "RosaceaFix") directly into your trademarked brand name to prevent regulatory and ad-platform flags.' },
      { dimension: 'Euphonic Softness', detail: 'Words rich in L, M, S, and W sounds score 31% higher on perceived gentleness and skin compatibility.' }
    ],
    faqs: [
      {
        question: 'Should my skincare brand name sound clinical or natural?',
        answer: 'It depends on your hero ingredients and customer avatar. If you formulate with clinical actives (niacinamide, tretinoin, tranexamic acid, peptides), choose a structured clinical or lab-inspired name. If you focus on holistic barrier nourishment and botanicals, choose a warm, nature-derived or dewy name.'
      },
      {
        question: 'What are the best keywords to put into the Skincare Brand Name Generator?',
        answer: 'Try sensory and scientific root words like "dew, glow, biome, serum, derma, lumina, barrier, cellular, petal, or pure." You can blend these with "Modern" for clinical DTC vibes or "Classic" for luxury spa positioning.'
      },
      {
        question: 'How do I make sure my skincare name isn’t already trademarked?',
        answer: 'Cosmetics and skincare fall under USPTO International Class 003. Avoid overused single words like "Glow" or "Pure" on their own; instead, use our generator to create distinctive compound words or coined neologisms (like CeraVex, LumaDrop, or Retinova) and check domain availability with one click.'
      },
      {
        question: 'Can I audit my skincare brand’s Instagram presence too?',
        answer: 'Yes! Once you launch, use BrandGenius’s deterministic Brand Audit and Social Media Audit tools to evaluate your skincare brand’s phonetic clarity and Instagram conversion funnel.'
      }
    ]
  },

  // ==========================================
  // 7. ALCOHOL BRAND NAME GENERATOR
  // ==========================================
  {
    key: 'alcoholBrand',
    legacyKeys: ['alcohol-brand-name-generator'],
    navLabel: 'Alcohol & Spirits Names',
    category: 'Food, Beverage & Spirits',
    targetKeyword: 'alcohol brand name generator',
    slug: '/alcohol-brand-name-generator/',
    h1: 'Alcohol Brand Name Generator',
    subtitle: 'Distill commanding, top-shelf brand names for craft distilleries, small-batch bourbon, botanical gin, agave tequila, fine wineries, and RTD cocktail brands.',
    defaultIndustryInput: 'Alcohol',
    defaultKeywordsInput: 'cask, copper, botanical, reserve',
    defaultStyle: 'Classic',
    seoTitle: 'Alcohol Brand Name Generator: 500+ Spirits, Wine & Beer Ideas | BrandGenius',
    seoDescription: 'Generate unforgettable alcohol brand names for whiskey, tequila, gin, vodka, wine, and craft RTD cocktails. Free domain check & spirits naming guide.',
    phoneticInsight: 'In the beverage alcohol sector, phonetic weight dictates perceived aging and price point. Dark spirits (bourbon, scotch, añejo tequila) thrive on resonant, gravelly consonants (B, R, G, K) and heritage structures, while clear spirits and RTDs (gin, vodka, hard seltzer) favor crisp, effervescent sibilants and bright vowels.',
    marketPositioning: 'An alcohol brand name must pass the "Loud Bar Test" and the "Back-Bar Silhouette Test." Customers must be able to order your spirit by name from a bartender over 85-decibel music, and the typography on your glass bottle must command authority from 10 feet away behind the bar.',
    frameworks: [
      {
        name: '01. Cask, Copper & Distillery Heritage Names',
        explanation: 'Evokes charred oak barrels, copper pot stills, master distillers, and generational recipes.',
        examples: ['CopperCask Reserve', 'IronStill Bourbon', 'Oak & Ember', 'Foundry Spirits', 'Cask 44', 'Hearthstone Distilling']
      },
      {
        name: '02. Botanical, Alchemy & Apothecary Gin / Aperitif Names',
        explanation: 'Mystical, herbarium-inspired names highlighting wild juniper, citrus peel, and botanical infusions.',
        examples: ['Alchemist Gin', 'Botanica 12', 'Velvet Elixir', 'Solstice Aperitivo', 'Verdant Still', 'Nocturne Botanicals']
      },
      {
        name: '03. Agave, Sun & Terroir Tequila / Mezcal Names',
        explanation: 'Celebrates volcanic highlands, roasted agave piñas, desert nights, and artisanal tahona crushing.',
        examples: ['Solara Agave', 'Sierra Oro', 'Fuego & Piedra', 'Casa Lumina', 'Cielo Añejo', 'Mesa Verde Spirits']
      },
      {
        name: '04. Modern Canned Cocktails, Hard Seltzers & Zero-Proof',
        explanation: 'Vibrant, social, sunshine-ready names designed for beach coolers, festival cans, and sober-curious aperitifs.',
        examples: ['WaveCrush', 'FizzyLuma', 'SunSip Social', 'ZestCraft', 'BreezeTonic', 'Mirage Non-Alc']
      },
      {
        name: '05. Ultra-Premium Vodka & Nightlife Bottle-Service Names',
        explanation: 'Crystalline, monolithic names that project glacial purity, filtration, and VIP exclusivity.',
        examples: ['Kryos Vodka', 'Obsidian Reserve', 'Aether Pure', 'Sovereign 10X', 'Zenith Spirit', 'Valour Crystal']
      }
    ],
    curatedCategories: [
      {
        archetype: 'Whiskey, Bourbon & Rye Brand Names',
        description: 'Rich, storied names for small-batch American bourbon, rye, and single-malt scotch.',
        names: ['Blackwood Cask', 'Copper Anvil', 'Sovereign Oak', 'Ember & Grain', 'Hollow Creek Reserve', 'Gilded Barrel', 'IronHorse Spirits', 'Veritas Bourbon', 'Old Meridian', 'Rust & Rye', 'Pinnacle Still', 'Timberline Proof']
      },
      {
        archetype: 'Botanical Gin, Vodka & Clear Spirit Names',
        description: 'Crisp, aromatic names for craft gin, glacial vodka, and artisanal white rum.',
        names: ['Lumina Gin', 'FrostVessel', 'Botanic Theory', 'Aether Distilling', 'SilverCanopy', 'CrystalMeridian', 'Juniper & Mist', 'Vanguard Vodka', 'PureAlchemy', 'Glasshouse Spirits', 'Nordic Drop', 'Zephyr Gin']
      },
      {
        archetype: 'Tequila, Mezcal & Agave Spirit Names',
        description: 'Warm, prestigious names for blanco, reposado, añejo, and artisanal mezcal.',
        names: ['Casa Solis', 'Alma de Cobre', 'Tierra Dorada', 'Sombra Real', 'Veloce Agave', 'Monte Estrella', 'Fuego Noble', 'Cielo Puro', 'Oro del Valle', 'Santo Volcán', 'Luna Brava', 'Reserva del Sol']
      },
      {
        archetype: 'RTD Canned Cocktails, Craft Beer & Non-Alcoholic Spirits',
        description: 'Playful, refreshing names for ready-to-drink spritzes, seltzers, and botanical zero-proof drinks.',
        names: ['CitrusWave', 'LumaSpritz', 'Halcyon Social', 'Daybreak Tonic', 'VividPour', 'Oasis Fizz', 'Kinetix Brew', 'GoldenHour Can', 'Driftwood Ale', 'Velvet Hop', 'Nomad Spritz', 'Botanic Zero']
      }
    ],
    auditCriteria: [
      { dimension: 'The "Loud Bar Call" Test', detail: 'When a patron orders a "House [Your Brand] and Tonic," the name must have distinct stressed syllables so bartenders never mishear it.' },
      { dimension: 'TTB COLA & Class 32/33 Compliance', detail: 'Alcohol labels require TTB (Alcohol and Tobacco Tax and Trade Bureau) approval and Class 32 (Beer/Non-Alc) or Class 33 (Wines & Spirits) clearance—avoid misleading geographic origin terms.' },
      { dimension: 'Embossed Glass Bottle Presence', detail: 'Names with 6–10 letters emboss cleanly into heavy-bottomed flint glass bottles.' }
    ],
    faqs: [
      {
        question: 'What makes a great alcohol or liquor brand name?',
        answer: 'Top-performing liquor names pass the "Bar Call Test"—they are easy to pronounce in a noisy room, carry a strong sense of place or craft (like Casamigos, Hendrick’s, or Bulleit), and evoke a clear mood on the bottle label.'
      },
      {
        question: 'Can I generate names for non-alcoholic spirits and RTD canned cocktails?',
        answer: 'Yes! Enter keywords like "botanical, spritz, tonic, oasis, or zero" and select "Modern" or "Creative" to generate fresh, lifestyle-driven names for canned cocktails, hard seltzers, and functional zero-proof aperitifs.'
      },
      {
        question: 'Are there legal restrictions on naming an alcohol brand?',
        answer: 'Yes. Under TTB regulations and international treaties, you cannot use protected geographic indications (such as Champagne, Cognac, Scotch, or Tequila) unless your liquid is produced in those designated regions according to strict standards. Keep your brand name distinct from protected appellations.'
      },
      {
        question: 'How do I check if my alcohol brand domain is available?',
        answer: 'Click the "Check Domain" button next to any generated spirit name above to verify .com availability instantly, or add modifiers like "Spirits.com" or "Distilling.com".'
      }
    ]
  },

  // ==========================================
  // 8. FITNESS BRAND NAME GENERATOR
  // ==========================================
  {
    key: 'fitnessBrand',
    legacyKeys: ['fitness-brand-name-generator'],
    navLabel: 'Fitness Brand Names',
    category: 'Fitness, Health & Wellness',
    targetKeyword: 'fitness brand name generator',
    slug: '/fitness-brand-name-generator/',
    h1: 'Fitness Brand Name Generator',
    subtitle: 'Generate high-intensity, motivating fitness brand names for gyms, athletic equipment, workout apps, CrossFit boxes, and performance coaching brands.',
    defaultIndustryInput: 'Fitness',
    defaultKeywordsInput: 'strength, kinetic, endurance, apex',
    defaultStyle: 'Modern',
    seoTitle: 'Fitness Brand Name Generator: 500+ Gym & Workout Name Ideas | BrandGenius',
    seoDescription: 'Build a powerful fitness brand name for your gym, workout app, fitness equipment, or coaching business. Free domain checks & fitness naming strategy.',
    phoneticInsight: 'High-performance fitness nomenclature relies on muscular, high-impact plosives (P, B, T, K, G) and vibrant fricatives (F, V, X, Z) combined with short vowel nuclei. These sounds trigger physiological arousal, discipline, and forward momentum.',
    marketPositioning: 'The modern fitness landscape spans everything from heavy-iron strength facilities and connected smart-home gym hardware to boutique Pilates reformers and AI-free deterministic training trackers. Your fitness brand name must inspire immediate action and community pride.',
    frameworks: [
      {
        name: '01. Peak Performance & Athletic Grit Names',
        explanation: 'Built for strength training, CrossFit boxes, HYROX clubs, and elite athletic conditioning.',
        examples: ['ApexForge', 'IronKinetic', 'TitanStride', 'Valour Athletics', 'SummitGrind', 'Vanguard Barbell']
      },
      {
        name: '02. Connected Fitness Tech & Workout App Names',
        explanation: 'Sleek, metric-driven, 1-to-2 word names ideal for App Store icons, wearables, and smart gym gear.',
        examples: ['PulseCore', 'Kinetix Fit', 'VeloTrack', 'OptiRep', ' TempoSync', 'StriveOS']
      },
      {
        name: '03. Mindful Movement, Pilates & Boutique Studio Names',
        explanation: 'Fluid, sculptural names for reformer Pilates, hot yoga, barre, and recovery sanctuaries.',
        examples: ['Forma Studio', 'LumaFlow', 'Sculpt & Breathe', 'Aether Movement', 'Solstice Pilates', 'Align Sanctum']
      },
      {
        name: '04. Community Run Clubs & Endurance Tribes',
        explanation: 'Inclusive, momentum-focused names for marathon crews, cycling pelotons, and triathlon brands.',
        examples: ['PaceSyndicate', 'Cadence Club', 'StrideCollective', 'Meridian Run', 'Velocity Crew', 'Endure Together']
      },
      {
        name: '05. Home Gym Equipment & Recovery Hardware Names',
        explanation: 'Industrial-grade names for adjustable dumbbells, cold plunges, infrared saunas, and massage guns.',
        examples: ['IronCore Systems', 'CryoPeak', 'FlexFoundry', 'KineticRack', 'zenith Recovery', 'PulseGun']
      }
    ],
    curatedCategories: [
      {
        archetype: 'Strength Gym, CrossFit & Athletic Club Names',
        description: 'Commanding names for training facilities, barbell clubs, and performance centers.',
        names: ['IronApex Gym', 'Foundry Athletics', 'TitanCore Fitness', 'Valour Strength', 'Pinnacle Performance', 'Anvil Training', 'Grit & Grace', 'Sovereign Barbell', 'Monolith Gym', 'Vanguard Fit', 'Forge & Flex', 'Bastion Athletics']
      },
      {
        archetype: 'Fitness App, Wearable & Coaching Brand Names',
        description: 'Punchy, digital-native names for training programs, trackers, and online coaching.',
        names: ['RepLogic', 'PulsePath', 'Kinetix Coach', 'StriveMetrics', 'VeloFit', 'OptimaTrain', 'MacroSync', 'EndureLab', 'PeakHabit', 'MomentumFit', 'CoreShift', 'ZettaFit']
      },
      {
        archetype: 'Boutique Pilates, Yoga & Recovery Studio Names',
        description: 'Elevated, aesthetic names for reformer studios, longevity lounges, and wellness spaces.',
        names: ['Sculpt Theory', 'Lumina Pilates', 'Aura Movement', 'Soma Studio', 'Veritas Flow', 'Solara Reform', 'Velvet Core', 'Sanctuary Sweat', 'Equinoxis', 'Poise & Pulse', 'TerraFlow', 'Cadence Reformer']
      },
      {
        archetype: 'Fitness Gear, Resistance Bands & Equipment Names',
        description: 'Durable, high-trust names for e-commerce fitness products and accessories.',
        names: ['FlexForge', 'GripTitan', 'AeroLift', 'KineticGear', 'ProStride', 'CoreVessel', 'VoltAthletics', 'PrimeKettle', 'UltraResistance', 'ZenithMat', 'PowerLoom', 'VertexEquip']
      }
    ],
    auditCriteria: [
      { dimension: 'App Icon & Apparel Embroidery Fit', detail: 'Fitness brands live on smartphone home screens and gym apparel—names with 4–8 letters scale cleanly across both mediums.' },
      { dimension: 'Motivational Sound Symbolism', detail: 'Active verbs and upward spatial metaphors (Apex, Rise, Ascend, Strive, Peak, Elevate) boost ad click-through rates in fitness verticals.' },
      { dimension: 'Multi-Category Extensibility', detail: 'Avoid naming your brand "Joe’s Kettlebells" if you plan to expand into supplements, apparel, or digital programs.' }
    ],
    faqs: [
      {
        question: 'How do I create a catchy fitness brand name?',
        answer: 'Focus on transformation and kinetic energy rather than generic words like "Gym" or "Workout." Combine action-oriented roots (Stride, Pulse, Forge, Sculpt, Endure, Apex) with clean suffixes or studio descriptors using our generator above.'
      },
      {
        question: 'What is the difference between naming a strength gym vs. a boutique Pilates studio?',
        answer: 'Strength and conditioning brands perform best with hard, industrial plosives (Forge, Iron, Titan, Apex, Grit), whereas boutique Pilates, yoga, and recovery studios attract higher retention with fluid, anatomical, and luminous names (Soma, Forma, Lumina, Poise, Cadence).'
      },
      {
        question: 'Should I put "Fitness" or "Athletics" at the end of my brand name?',
        answer: 'Adding "Athletics", "Club", "Lab", or "Performance" is a proven strategy for securing an exact-match .com domain and clarifying your niche while keeping your primary logo mark a single punchy word.'
      },
      {
        question: 'How can I audit my fitness brand’s social media presence?',
        answer: 'Fitness is one of the most visual industries on Instagram. Use our Brand Audit and Instagram Audit tools to evaluate your brand name’s phonetic punch and optimize your fitness profile’s engagement and bio conversion.'
      }
    ]
  },

  // ==========================================
  // 9. MAKEUP BRAND NAME GENERATOR
  // ==========================================
  {
    key: 'makeupBrand',
    legacyKeys: ['makeup-brand-name-generator'],
    navLabel: 'Makeup Brand Names',
    category: 'Beauty & Cosmetics',
    targetKeyword: 'makeup brand name generator',
    slug: '/makeup-brand-name-generator/',
    h1: 'Makeup Brand Name Generator',
    subtitle: 'Create editorial, high-pigment makeup brand names for lip gloss lines, inclusivity-first foundation ranges, indie eyeshadow palettes, and celebrity artistry labels.',
    defaultIndustryInput: 'Makeup',
    defaultKeywordsInput: 'velvet, pigment, chroma, rouge',
    defaultStyle: 'Creative',
    seoTitle: 'Makeup Brand Name Generator: 500+ Cosmetics & Artistry Ideas | BrandGenius',
    seoDescription: 'Generate glamorous, editorial, and clean makeup brand names for lipsticks, palettes, and artistry lines. Free domain check & makeup naming strategy.',
    phoneticInsight: 'Makeup nomenclature thrives on textural and chromatic sound symbolism: velvety labials and fricatives (V, F, M, L, Sh) evoke smooth application and satin finishes, while sharp, rhythmic patterns (Nars, Mac, Fenty, Kosas, Ilia) create instant editorial authority.',
    marketPositioning: 'From viral lip oils and clean "no-makeup makeup" tints to avant-garde editorial pigment houses, a makeup brand name must look iconic hot-stamped onto a sleek compact mirror, lipstick bullet, or mascara wand.',
    frameworks: [
      {
        name: '01. Editorial Artistry & Studio Names',
        explanation: 'Inspired by backstage Fashion Week makeup artists, high-fashion photography, and pro pigment mastery.',
        examples: ['Atelier Chroma', 'Studio Velour', 'Pigment & Poise', 'Backstage Lumiere', 'Canvas Artistry', 'Haus of Shade']
      },
      {
        name: '02. Clean "No-Makeup" & Skin-Tint Names',
        explanation: 'Fresh, effortless, skin-first names for serum foundations, cream blushes, and tinted lip balms.',
        examples: ['SecondSkin Co.', 'BareFlush', 'DewTint', 'FeatherLight Beauty', 'SoftFocus Lab', 'Sunlit Cheek']
      },
      {
        name: '03. Bold, Euphoria-Era & Indie Color Cosmetics',
        explanation: 'Playful, hyper-expressive, high-drama names for holographic highlighters, graphic liners, and glosses.',
        examples: ['ChromaRiot', 'NeonVelvet', 'HyperGloss', 'Starlight Pigment', 'PrismLash', 'Electric Pout']
      },
      {
        name: '04. Parisian & Italian Glamour Names',
        explanation: 'Romantic, timeless names evoking red lipstick, gilded compacts, and cinematic elegance.',
        examples: ['Maison Rouge', 'Belle Éclat', 'Velours d’Or', 'Veloce Lip', 'Palazzo Glamour', 'Sovereign Silk']
      },
      {
        name: '05. Short 4-to-5 Letter Cult Makeup Names',
        explanation: 'Follows the blueprint of Kosas, Ilia, Merit, Saie, and Nars—short, symmetrical, and unmistakable.',
        examples: ['Veloa', 'Luvra', 'Kroma', 'Soria', 'Zelva', 'Mirva']
      }
    ],
    curatedCategories: [
      {
        archetype: 'Lip Gloss, Lip Oil & Lipstick Brand Names',
        description: 'Sensory, kissable names for lip products, liners, and plush matte formulas.',
        names: ['VelvetPout', 'GlazeTheory', 'NectarLip', 'PlushRouge', 'MirrorGloss Co.', 'SilkKiss Atelier', 'Crème & Coral', 'LumaLip', 'SatinDrop', 'Cherie Glaze', 'Bitten Velvet', 'HoneyDew Lip']
      },
      {
        archetype: 'Clean & Minimalist Everyday Makeup Names',
        description: 'Effortless, modern names for clean beauty compacts, brow gels, and cream bronzers.',
        names: ['MeritGlow', 'PureCanvas', 'SoftSculpt', 'AuraTint', 'DailyFlush', 'Linen & Blush', 'KindSkin Makeup', 'Solara Tint', 'BarelyVelvet', 'CloudCompact', 'TrueTone Lab', 'Lucid Flush']
      },
      {
        archetype: 'Pro Makeup Artist & Lash / Brow Brand Names',
        description: 'Precision, high-glam names for false lashes, brow lamination kits, and pro brushes.',
        names: ['Arch & Lash', 'Vanguard Artistry', 'Sculpted Brow Co.', 'Flutter Atelier', 'Obsidian Liner', 'ProStroke Studio', 'LashSovereign', 'Precision Gaze', 'Noir Mascara', 'Velour Lash', 'Iconic Arch', 'Muse Artistry']
      },
      {
        archetype: 'Avant-Garde & Indie Eyeshadow Palette Names',
        description: 'Creative, multidimensional names for color cosmetics and shimmer pigments.',
        names: ['ChromaVerse', 'Alchemy Palette', 'Celestial Dust', 'Mirage Pigment', 'NovaShimmer', 'Prism & Co.', 'Stardust Velour', 'KaleidoGlam', 'Eclipse Chroma', 'Aura Alchemy', 'VividMuse', 'Luminary Shades']
      }
    ],
    auditCriteria: [
      { dimension: 'Lipstick Tube & Compact Embossing', detail: 'A lipstick cap offers less than 15mm of stamping space. Short names (4–7 letters) or clean 2-letter monograms are essential for hardware tooling.' },
      { dimension: 'Shade-Naming Architecture', detail: 'A great parent makeup brand name sets the creative universe for naming hundreds of individual SKUs and shades.' },
      { dimension: 'Social Tag & Tutorial Pronunciation', detail: 'Beauty creators mention makeup brands aloud in GRWM (Get Ready With Me) videos—unambiguous pronunciation prevents lost word-of-mouth.' }
    ],
    faqs: [
      {
        question: 'How do I choose a name for my makeup line?',
        answer: 'Decide whether your makeup line is artistry-driven (bold pigments, full glam), clean/minimalist (skin-first cream formulas), or category-specific (lip oils or lashes). Use our Makeup Brand Name Generator above with keywords matching your texture and finish (e.g., velvet, glaze, chroma, flush).'
      },
      {
        question: 'Why are so many modern makeup brands 4 or 5 letters long?',
        answer: 'Brands like NARS, MAC, ILIA, SAIE, KOSAS, and MERIT use 4–5 letter names because they fit horizontally on tiny mascara tubes and lip gloss wands without shrinking the font size, creating massive visual impact in flat-lay product photography.'
      },
      {
        question: 'Should I put "Beauty" or "Cosmetics" at the end of my makeup brand name?',
        answer: 'Even if your logo only displays the core word (e.g., "Velora"), adding "Beauty" or "Cosmetics" in your legal trade name and domain (VeloraBeauty.com) makes it much easier to secure the .com domain and rank on Google.'
      },
      {
        question: 'How can I audit my makeup brand name before manufacturing packaging?',
        answer: 'Click any name generated above to check .com domain availability, then run your favorite candidate through the BrandGenius Brand Audit tool to score its phonetic clarity and linguistic appeal.'
      }
    ]
  },

  // ==========================================
  // 10. BEAUTY BRAND NAME GENERATOR
  // ==========================================
  {
    key: 'beautyBrand',
    legacyKeys: ['beauty-brand-name-generator'],
    navLabel: 'Beauty Brand Names',
    category: 'Beauty & Cosmetics',
    targetKeyword: 'beauty brand name generator',
    slug: '/beauty-brand-name-generator/',
    h1: 'Beauty Brand Name Generator',
    subtitle: 'Discover radiant, cross-category beauty brand names built to scale across skincare, haircare, color cosmetics, body care, and aesthetic wellness.',
    defaultIndustryInput: 'Beauty',
    defaultKeywordsInput: 'radiant, aura, bloom, lumiere',
    defaultStyle: 'Creative',
    seoTitle: 'Beauty Brand Name Generator: 500+ Iconic Beauty Name Ideas | BrandGenius',
    seoDescription: 'Generate unforgettable beauty brand names for clean beauty startups, salons, spas, and multi-category beauty lines. Instant domain checks & SEO guide.',
    phoneticInsight: 'Cross-category beauty brands rely on resonant, balanced vowel harmony (A-I-A, O-A, E-I) and gentle bilabial or liquid onsets (B, M, L, V, S) that convey self-care, transformation, and sensory indulgence.',
    marketPositioning: 'Unlike niche single-product labels, an umbrella beauty brand name must have the elastic brand equity to stretch across facial serums, body oils, fragrance mists, beauty tools, and salon services without feeling boxed in.',
    frameworks: [
      {
        name: '01. Radiant & Luminous Aura Names',
        explanation: 'Captures the universal outcome of great beauty products: light, confidence, and inner-to-outer glow.',
        examples: ['Lumina Beauty', 'Aura Bloom', 'Solara Glow', 'Radiant Vessel', 'Halcyon Beauty', 'Éclat Rituals']
      },
      {
        name: '02. Holistic Self-Care & Ritual Names',
        explanation: 'Positions beauty as a daily sanctuary and emotional wellness practice rather than vanity.',
        examples: ['Sanctuary Beauty', 'Sacred Layer', 'Stillness Co.', 'Kindred Ritual', 'Solace Aesthetics', 'Mindful Muse']
      },
      {
        name: '03. Modern Inclusive & Cult-Community Names',
        explanation: 'Warm, human, democratic names that celebrate individuality and authentic texture.',
        examples: ['TrueHue Beauty', 'EveryMuse', 'CommonGlow', 'Unfiltered Lab', 'KindForm', 'RealAura']
      },
      {
        name: '04. Luxury Med-Spa & Aesthetic Clinic Names',
        explanation: 'Elevated, trustworthy names for facial bars, laser clinics, lash studios, and beauty lounges.',
        examples: ['Atelier Esthetics', 'Maison Glow', 'Sovereign MedSpa', 'Veritas Beauty Lounge', 'Velvet Aesthetics', 'Pinnacle Skin & Beauty']
      },
      {
        name: '05. Coined Euphonic Beauty Names',
        explanation: 'Melodious invented words ending in -a, -ia, or -elle that sound timeless and globally ownable.',
        examples: ['Avelia', 'Luminaia', 'Sorelle', 'Velora', 'Eloria', 'Mirabelle']
      }
    ],
    curatedCategories: [
      {
        archetype: 'Clean & Conscious Multi-Category Beauty Names',
        description: 'Fresh, sustainable names for clean beauty lines spanning skin, body, and makeup.',
        names: ['PureAura Beauty', 'Bloom & Vessel', 'Verdant Muse', 'PetalTheory', 'Solstice Beauty Co.', 'DewSanctuary', 'Botanica Éclat', 'ClearHorizon Beauty', 'Nectar & Co.', 'TerraGlow', 'Linen Beauty', 'LivingLuma']
      },
      {
        archetype: 'Prestige & Luxury Beauty House Names',
        description: 'High-end names designed for department store counters, Harrods, and Sephora.',
        names: ['Maison Aurelia', 'Atelier Velour', 'Sovereign Beauty', 'Lumiere Royale', 'Orchid & Gold', 'Veritas Esthetics', 'Palazzo Beauty', 'Gilded Muse', 'Celeste Privé', 'Valour Beauty', 'Élan Vital', 'Crown & Petal']
      },
      {
        archetype: 'Beauty Salon, Med-Spa & Studio Names',
        description: 'Inviting, aspirational names for beauty bars, aesthetic clinics, and bridal studios.',
        names: ['The Glow Sanctuary', 'Velvet Rose Studio', 'Halo Aesthetics', 'Lumina MedSpa', 'Blush & Botanicals', 'Muse Beauty Bar', 'Serenity Atelier', 'Poise & Polish', 'GoldenHour Salon', 'Radiance Collective', 'Opal Beauty Lounge', 'Haven Esthetics']
      },
      {
        archetype: 'Beauty Tools, Devices & Accessories Names',
        description: 'Sleek names for LED masks, gua sha stones, silk pillowcases, and sonic cleansers.',
        names: ['SculptAura', 'LumaPulse', 'SilkSanctum', 'QuartzRitual', 'SonicDew', 'VeloGlow', 'ContourLab', 'RoseQuartz Co.', 'OptiBeauty', 'Jade & Light', 'HaloDevice', 'PureSculpt']
      }
    ],
    auditCriteria: [
      { dimension: 'Category Elasticity', detail: 'Ensure your beauty brand name does not lock you into a single body part (like "Lip" or "Lash") if your 3-year roadmap includes body care or fragrance.' },
      { dimension: 'Visual Unboxing Aesthetics', detail: 'Beauty is the #1 unboxing category on TikTok and Instagram—symmetric typography on inner box flaps drives organic user-generated content.' },
      { dimension: 'Phonetic Warmth', detail: 'Open vowel endings (-a, -e, -o) create an inviting, nurturing brand persona.' }
    ],
    faqs: [
      {
        question: 'How do I come up with a unique beauty brand name?',
        answer: 'Instead of combining generic words like "Pretty" or "Glam," look for words that describe light, texture, botanicals, or emotion (such as Aura, Lumina, Velvet, Sanctuary, Dew, or Muse). Enter your favorite mood keywords into our Beauty Brand Name Generator above for tailored combinations.'
      },
      {
        question: 'What is the difference between a Beauty brand name, a Makeup brand name, and a Skincare brand name?',
        answer: 'A Beauty brand name serves as a broad umbrella that can house hair, body, skin, fragrance, and color products (like Glossier, Rare Beauty, or Fenty Beauty). Skincare names often lean more clinical/cellular, while Makeup names lean more chromatic and artistry-focused.'
      },
      {
        question: 'How do I check if a beauty brand name is available on Instagram and .com?',
        answer: 'Click "Check Domain" next to any generated name above to inspect .com availability, and use our deterministic Brand Audit tool to evaluate digital readiness.'
      },
      {
        question: 'Can I download a PDF of my generated beauty brand names?',
        answer: 'Yes! Once you generate a list of beauty brand names using the form above, click the "Download PDF" button to export a formatted presentation sheet for your co-founders or agency clients.'
      }
    ]
  },

  // ==========================================
  // 11. COSMETIC BRAND NAME GENERATOR
  // ==========================================
  {
    key: 'cosmeticBrand',
    legacyKeys: ['cosmetic-brand-name-generator'],
    navLabel: 'Cosmetic Brand Names',
    category: 'Beauty & Cosmetics',
    targetKeyword: 'cosmetic brand name generator',
    slug: '/cosmetic-brand-name-generator/',
    h1: 'Cosmetic Brand Name Generator',
    subtitle: 'Formulate authoritative, lab-to-vanity cosmetic brand names for cosmeceuticals, private-label cosmetic lines, derm-grade treatments, and global beauty labs.',
    defaultIndustryInput: 'Cosmetic',
    defaultKeywordsInput: 'formula, elixir, radiant, lab',
    defaultStyle: 'Modern',
    seoTitle: 'Cosmetic Brand Name Generator: 500+ Cosmeceutical & Lab Ideas | BrandGenius',
    seoDescription: 'Create scientific, luxury, and indie cosmetic brand names for cosmeceuticals, color cosmetics, and aesthetic labs. Free domain check & naming guide.',
    phoneticInsight: 'The word "Cosmetic" derives from the Greek "kosmos" (order, harmony, and ornament). High-converting cosmetic brand names balance scientific precision (sharp consonants like C, K, X, T) with aesthetic harmony (fluid vowels and liquid consonants).',
    marketPositioning: 'Whether you are launching a cosmeceutical line backed by cosmetic chemists, a private-label vegan color cosmetics brand, or an injectable/aesthetic clinic product line, your cosmetic brand name must bridge the gap between laboratory credibility and luxury desire.',
    frameworks: [
      {
        name: '01. Cosmeceutical & Formulation Lab Names',
        explanation: 'Highlights molecular science, cosmetic chemistry, biocompatibility, and clinical testing.',
        examples: ['Formula Lumina', 'Cosmetix Lab', 'DermaChroma', 'Molecular Silk', 'BioElixir Cosmetics', 'Precision Aesthetic']
      },
      {
        name: '02. Prestige European Cosmetic Houses',
        explanation: 'Evokes Parisian, Milanese, or Geneva-based cosmetic laboratories and heritage luxury.',
        examples: ['Laboratoire Éclat', 'Maison Cosmétique', 'Geneva Derma', 'Atelier Lumiere', 'Sovereign Cosmetics', 'Veritas Esthetique']
      },
      {
        name: '03. Clean, Cruelty-Free & Botanical Cosmetics',
        explanation: 'Communicates plant-derived pigments, non-toxic preservatives, and sustainable packaging.',
        examples: ['PureFlora Cosmetics', 'Botanica Chroma', 'VerdeElixir', 'EarthSilk Cosmetics', 'PetalLab', 'CleanVessel']
      },
      {
        name: '04. High-Glam & Luxe Color Cosmetics',
        explanation: 'Opulent, red-carpet names for high-coverage foundations, setting powders, and contour kits.',
        examples: ['VelvetGold Cosmetics', 'Opulence Beauty', 'Gilded Silk', 'RoyalChroma', 'Starlight Cosmetics', 'Mirage Luxe']
      },
      {
        name: '05. Modern Neo-Latin Coined Cosmetic Names',
        explanation: 'Distinctive, trademark-ready coined names built for global Class 003 registration.',
        examples: ['Kosmera', 'Velonix', 'Lumivex', 'Chromia', 'Dermlux', 'Aureliax']
      }
    ],
    curatedCategories: [
      {
        archetype: 'Cosmeceutical & Aesthetic Clinic Brand Names',
        description: 'Clinical, doctor-trusted names for post-procedure cosmetics, peptides, and serums.',
        names: ['DermaVanguard', 'AestheticCore', 'CelluLux Cosmetics', 'BioSilk Lab', 'Clinique Veritas', 'Optima Derma', 'LuminaMed Cosmetics', 'Synergy Esthetics', 'PureMatrix Lab', 'Epiderm Royale', 'NovaFormula', 'dermalÉclat']
      },
      {
        archetype: 'Private-Label & Indie Color Cosmetic Names',
        description: 'Trend-forward, high-margin names for lip kits, concealers, and complexion lines.',
        names: ['ChromaSilk', 'Velour & Co.', 'PrismCosmetics', 'AuraPigment', 'SatinTheory', 'LuxeLayer Cosmetics', 'vividMuse', 'HaloComplexion', 'SilkDrop Cosmetics', 'TrueShade Lab', 'RadiantCrest', 'VelvetBloom']
      },
      {
        archetype: 'Organic, Mineral & Vegan Cosmetic Brand Names',
        description: 'Pure, earth-conscious names for mineral powders, botanical tints, and clean formulas.',
        names: ['MineralDew', 'TerraChroma', 'BotanicSilk', 'PurePetal Cosmetics', 'Solara Mineral', 'GreenElixir Co.', 'FloraLuma', 'Quartz & Clay', 'EdenCosmetics', 'KindEarth Beauty', 'Canopy Cosmetics', 'BareMineralis']
      },
      {
        archetype: 'Global B2B Cosmetic Lab & Formulation Names',
        description: 'Authoritative corporate names for contract manufacturers, cosmetic chemists, and R&D labs.',
        names: ['Apex Cosmetic Labs', 'Vertex Formulations', 'OmniChroma R&D', 'Pinnacle Cosmeceuticals', 'Quantum Esthetics', 'Synthesis Beauty Labs', 'Meridian Cosmetics', 'CoreFormula Group', 'Vanguard DermLabs', 'Innovate Cosmetix', 'PrimeElixir Labs', 'Zenith Formulation']
      }
    ],
    auditCriteria: [
      { dimension: 'INCI & Packaging Compliance', detail: 'Cosmetic labels require strict ingredient (INCI) and net-weight disclosures—a compact, legible brand wordmark leaves breathing room for mandatory regulatory text.' },
      { dimension: 'Scientific vs. Sensory Balance', detail: 'Names that are 100% sterile sound like generic pharmaceuticals; pairing a scientific root (Bio, Derma, Lab) with a sensory root (Silk, Lumina, Velvet) maximizes conversion.' },
      { dimension: 'International Trademark Class 003', detail: 'Class 003 covers cosmetics, perfumery, and cleaning preparations—coined compounds have the highest approval velocity.' }
    ],
    faqs: [
      {
        question: 'What is the best naming strategy for a new cosmetic brand?',
        answer: 'Pair efficacy with elegance. Modern consumers scrutinize ingredient lists, so cosmetic brand names that blend scientific credibility (Lab, Formula, Bio, Derma) with sensory luxury (Silk, Lumiere, Velvet, Aura) outperform generic names.'
      },
      {
        question: 'Should I use "Cosmetics" or "Beauty" in my brand name?',
        answer: '"Cosmetics" carries stronger authority for color makeup, foundations, and cosmeceutical laboratories (e.g., IT Cosmetics, Kylie Cosmetics, MAC Cosmetics), whereas "Beauty" feels broader for lifestyle and wellness cross-overs.'
      },
      {
        question: 'How do I generate cosmetic brand names for a specific niche like mineral makeup?',
        answer: 'Enter your niche keywords—such as "mineral, quartz, pure, botanical, or peptide"—into the Keywords field in the generator form above and click Generate Names.'
      },
      {
        question: 'Can I audit my existing cosmetic brand name?',
        answer: 'Yes! Visit our Brand Audit tool to run a complete deterministic analysis of your cosmetic brand name across Linguistic Efficiency, Phonetic Clarity, Digital Readiness, and Strategic Intent.'
      }
    ]
  },

  // ==========================================
  // 12. JEWELRY BRAND NAME GENERATOR
  // ==========================================
  {
    key: 'jewelryBrand',
    legacyKeys: ['jewelry-brand-name-generator'],
    navLabel: 'Jewelry Brand Names',
    category: 'Luxury, Jewelry & Lifestyle',
    targetKeyword: 'jewelry brand name generator',
    slug: '/jewelry-brand-name-generator/',
    h1: 'Jewelry Brand Name Generator',
    subtitle: 'Craft luminous, heirloom-worthy jewelry brand names for fine diamond houses, demi-fine gold vermeil labels, bespoke engagement ring ateliers, and artisan silversmiths.',
    defaultIndustryInput: 'Jewelry',
    defaultKeywordsInput: 'gold, lumiere, carat, aurelia',
    defaultStyle: 'Classic',
    seoTitle: 'Jewelry Brand Name Generator: 500+ Fine & Demi-Fine Ideas | BrandGenius',
    seoDescription: 'Generate timeless, elegant jewelry brand names for fine jewelry, diamond ateliers, demi-fine gold brands, and handmade accessories. Free domain check.',
    phoneticInsight: 'Jewelry naming relies on "crystalline phonetics"—a blend of bright front vowels and liquid sonorants (L, R, M, N) punctuated by delicate stops (C, T, D, V) that mirror the refraction of light through faceted gemstones and precious metals.',
    marketPositioning: 'A jewelry brand name faces the ultimate physical constraint: it must be laser-hallmarked inside a 2mm ring band or stamped onto a tiny lobster clasp tag, while also looking regal embossed in gold foil on a velvet ring box.',
    frameworks: [
      {
        name: '01. Fine Diamond & Place Vendôme Atelier Names',
        explanation: 'Projects generational craftsmanship, GIA-certified brilliance, and haute joaillerie prestige.',
        examples: ['Maison Aurelia', 'Atelier Carat', 'Veritas Fine Jewelry', 'Lumiere Joaillerie', 'Sovereign Solitaire', 'Château d’Or']
      },
      {
        name: '02. Modern Demi-Fine & Everyday Stacking Jewelry',
        explanation: 'Chic, effortless names for 14k solid gold, gold vermeil, and waterproof everyday chains and hoops.',
        examples: ['Gilded Layer', 'LumaGold', 'Aura & Chain', 'Solara Vermeil', 'DailyCarat', 'Velvet Clasp']
      },
      {
        name: '03. Celestial, Mythological & Talismanic Names',
        explanation: 'Inspired by constellations, ancient signet rings, birthstones, and protective amulets.',
        examples: ['Celestia Gems', 'Astral Signet', 'Solstice Gold', 'Orion’s Vault', 'Seraphina Jewels', 'Lyra & Stone']
      },
      {
        name: '04. Lab-Grown Diamond & Ethical Modernists',
        explanation: 'Clean, radiant, forward-looking names for sustainable conflict-free diamonds and recycled gold.',
        examples: ['PureCarat', 'Aether Diamonds', 'Clarity Lab', 'Prism Ethical', 'Lucid Gem', 'Origin Brilliance']
      },
      {
        name: '05. Bespoke Silversmith & Bohemian Artisan Names',
        explanation: 'Earthy, tactile names for hand-forged sterling silver, turquoise, pearls, and organic metalwork.',
        examples: ['Anvil & Opal', 'Hammered Moon', 'TerraStone Studio', 'Wildflower Silver', 'Saltwater Pearl Co.', 'Copper & Quartz']
      }
    ],
    curatedCategories: [
      {
        archetype: 'Fine Jewelry & Bridal Engagement Ring Brand Names',
        description: 'Timeless, high-trust names for diamond jewelers, custom engagement rings, and heirloom pieces.',
        names: ['Aurelia & Co.', 'Maison Solitaire', 'Veritas Diamonds', 'Lumiere Fine Gems', 'Sovereign Carat', 'Vanguard Joaillerie', 'Éclat d’Or', 'Noble Facet', 'Palazzo Jewels', 'Crown & Pavé', 'Valour Ring Co.', 'Eternity Atelier']
      },
      {
        archetype: 'Demi-Fine, Waterproof & Gold Vermeil Brand Names',
        description: 'Trendy, DTC-ready names for stackable rings, huggie hoops, and layered necklaces.',
        names: ['GoldTheory', 'LumaChain', 'Gilded Habit', 'Sunlit Vermeil', 'AuraStack', 'HoneyGold Studio', 'Midas Touch Co.', 'Veloce Gold', 'ShimmerLayer', 'BareCarat', 'Solis Jewelry', 'Lustre & Link']
      },
      {
        archetype: 'Celestial, Crystal & Spiritual Jewelry Names',
        description: 'Enchanting names for gemstone jewelry, zodiac pendants, and healing crystal designs.',
        names: ['Moonstone Sanctum', 'Celestial Crest', 'Starlight Talisman', 'Oracle Gems', 'Solstice Amulet', 'Aether & Opal', 'NovaQuartz', 'Cosmos Jewels', 'Mystic Facet', 'Eclipse Gold', 'Seraphim Stone', 'Zodiac Vault']
      },
      {
        archetype: 'Men’s Jewelry, Signet Rings & Heavy Silver Names',
        description: 'Bold, architectural names for sterling silver chains, signet rings, and cufflinks.',
        names: ['Obsidian Forge', 'IronSignet', 'Monolith Silver', 'Sovereign Guild', 'Vanguard Chains', 'Sterling Crest', 'Bastion Metals', 'Valour & Stone', 'Noir Silversmiths', 'TitanClasp', 'Heritage Signet', 'Foundry 925']
      }
    ],
    auditCriteria: [
      { dimension: 'Inner-Band Hallmark Legibility', detail: 'Jewelry falls under USPTO Class 14. Your core wordmark or monogram must be legible when laser-engraved at 1.5mm height inside a ring shank.' },
      { dimension: 'Gift-Moment Emotional Resonance', detail: 'Over 60% of fine jewelry purchases mark a milestone or gift—names with warm, romantic phonetics increase gift-buyer confidence.' },
      { dimension: 'High-Ticket Trust Signals', detail: 'Avoid kitschy or overly trendy slang if selling pieces over $500; use Latin, French, or clean architectural roots.' }
    ],
    faqs: [
      {
        question: 'How do I choose a name for my jewelry business?',
        answer: 'Match your phonetic style to your price point and material. Fine diamond and 18k gold jewelry thrives on heritage, surname, or French/Latin structures (like Cartier, Bulgari, Mejuri, or Aurelia). Demi-fine and handmade jewelry performs great with evocative two-word combinations (like Gilded Layer or Moonstone Sanctum).'
      },
      {
        question: 'What words evoke gold and diamonds without sounding cliché?',
        answer: 'Use luminous and classical roots such as Aurelia (golden), Lumiere (light), Éclat (brilliance), Facet, Solis, Lustre, Sovereign, Pavilion, and Meridian.'
      },
      {
        question: 'Should I use "Jewelry" or "Jewels" or "Fine Jewelry" in my domain name?',
        answer: 'Adding "Jewelry", "Jewels", or "Gold" to your domain name (e.g., AureliaJewelry.com) is an excellent SEO and domain strategy when the single-word .com is already owned, and it immediately signals your product category in search results.'
      },
      {
        question: 'How can I evaluate my jewelry brand’s Instagram aesthetic and bio?',
        answer: 'Jewelry is a visual-first category. After choosing your name and checking its .com domain above, use our deterministic Brand Audit and Social Media Audit tools to optimize your brand identity.'
      }
    ]
  },

  // ==========================================
  // 13. CLOTHING BRAND NAME GENERATOR
  // ==========================================
  {
    key: 'clothingBrand',
    legacyKeys: ['clothing-brand-name-generator'],
    navLabel: 'Clothing Brand Names',
    category: 'Fashion & Apparel',
    targetKeyword: 'clothing brand name generator',
    slug: '/clothing-brand-name-generator/',
    h1: 'Clothing Brand Name Generator',
    subtitle: 'Generate versatile, woven-label-ready clothing brand names for contemporary apparel lines, capsule wardrobes, denim brands, and cut-and-sew startups.',
    defaultIndustryInput: 'Clothing',
    defaultKeywordsInput: 'thread, loom, silhouette, studio',
    defaultStyle: 'Modern',
    seoTitle: 'Clothing Brand Name Generator: 500+ Apparel Line Ideas | BrandGenius',
    seoDescription: 'Create memorable clothing brand names for apparel startups, boutique fashion lines, and DTC clothing brands. Free domain checks & naming playbook.',
    phoneticInsight: 'Successful clothing brand names balance tactile fabric phonetics (Loom, Thread, Weave, Knit, Silk) with clean, symmetrical typography that looks effortless on a woven neck label and hangtag.',
    marketPositioning: 'Whether you are building an elevated essentials brand, a heritage workwear line, or a contemporary women’s ready-to-wear label, your clothing brand name is the anchor of your brand equity.',
    frameworks: [
      {
        name: '01. Tactile Textile & Craftsmanship Names',
        explanation: 'Honors the raw materials, weave, and tailoring behind every garment.',
        examples: ['Loom & Line', 'GoldenThread Studio', 'WeaveTheory', 'Canvas & Stitch', 'PureGarment', 'NobleLoom']
      },
      {
        name: '02. Capsule Wardrobe & Minimalist Essentials',
        explanation: 'Clean, architectural names for timeless tees, trousers, and everyday uniform dressing.',
        examples: ['Uniform Standard', 'Form & Drape', 'DailySilhouette', 'CoreWardrobe', 'Basis Apparel', 'QuietThread']
      },
      {
        name: '03. Contemporary Boutique & Designer Labels',
        explanation: 'Elevated, editorial names that sound like a Copenhagen, Paris, or New York fashion studio.',
        examples: ['Atelier Vane', 'Studio Solara', 'Maison Linen', 'Aurelia Garments', 'Vanguard Attire', 'Élan Apparel']
      },
      {
        name: '04. Heritage Denim & Workwear Guild Names',
        explanation: 'Rugged, durable names built for leather waist patches and selvedge denim.',
        examples: ['IronLoom Co.', 'Foundry Denim', 'Anvil & Thread', 'Sovereign Goods', 'Timberline Apparel', 'Redwood Garment']
      },
      {
        name: '05. Short Coined Apparel Names',
        explanation: 'Distinctive 1-word coined names optimized for Class 25 trademark registration.',
        examples: ['Veloa', 'Tredon', 'Lumina', 'Kovex', 'Solvra', 'Modex']
      }
    ],
    curatedCategories: [
      {
        archetype: 'Minimalist & Capsule Clothing Brand Names',
        description: 'Refined names for elevated basics, organic cotton tees, and modern essentials.',
        names: ['PureForm Apparel', 'LinenTheory', 'Standard Issue Co.', 'QuietLoom', 'CoreSilhouette', 'MonoThread', 'NeutralState', 'DailyGarment', 'Poise Apparel', 'Clarity Cloth', 'TrueWeave', 'ModaMinimal']
      },
      {
        archetype: 'Contemporary Women’s & Boutique Apparel Names',
        description: 'Romantic, stylish names for dresses, resortwear, and contemporary collections.',
        names: ['Solstice Attire', 'Velvet & Vine', 'Aura Wardrobe', 'Muse & Meadow', 'Lumina Dress Co.', 'Sovereign Silk', 'Palazzo Linen', 'Orchid Garments', 'Seraphina Studio', 'Verona Apparel', 'Éclat Clothier', 'Celeste & Co.']
      },
      {
        archetype: 'Men’s Menswear, Tailoring & Workwear Names',
        description: 'Structured, authoritative names for menswear, overshirts, and heritage apparel.',
        names: ['Vanguard Clothiers', 'IronWeave Goods', 'Sterling & Tailor', 'Bastion Menswear', 'Crestline Garments', 'Foundry Stitch', 'Meridian Attire', 'Valour Supply', 'Sovereign Thread', 'Anvil Workwear', 'Noble Canvas', 'Apex Haberdashery']
      },
      {
        archetype: 'Youth, Gen-Z & Trend-Forward Apparel Names',
        description: 'High-energy names for social-first clothing brands and viral drops.',
        names: ['VibeLoom', 'FluxApparel', 'NovaThread', 'HyperGarment', 'PulseWardrobe', 'ShiftClothing', 'ZestAttire', 'KineticWear', 'NextWave Club', 'VividStitch', 'EchoApparel', 'OrbitCloth']
      }
    ],
    auditCriteria: [
      { dimension: 'Neck Label & Hangtag Geometry', detail: 'A clothing brand name must read clearly at 10pt size on a satin or cotton neck label.' },
      { dimension: 'Seasonal Elasticity', detail: 'Avoid seasonal words (like "Summer" or "Winter") unless you exclusively sell single-season resortwear or outerwear.' },
      { dimension: 'Class 25 Distinctiveness', detail: 'Apparel is the single most filed trademark class—pair distinctive nouns or coined words to secure registration.' }
    ],
    faqs: [
      {
        question: 'How do I come up with a good name for a clothing brand?',
        answer: 'Define your customer’s aspirational identity first. Minimalist buyers respond to structural words (Form, Standard, Loom, Theory), while boutique buyers respond to atmospheric or studio names. Enter 2–3 mood words in our Clothing Brand Name Generator above to create tailored options.'
      },
      {
        question: 'Should I include "Clothing" or "Apparel" in my brand name?',
        answer: 'Use a distinctive 1-or-2 word core name on your garment labels, and append "Apparel", "Clothing", or "Studio" for your website domain and legal entity.'
      },
      {
        question: 'How can I check if a clothing brand name is already used?',
        answer: 'Click "Check Domain" on any generated name above to test .com availability, then run your favorite name through our Brand Audit tool.'
      }
    ]
  },

  // ==========================================
  // 14. FASHION BRAND NAME GENERATOR
  // ==========================================
  {
    key: 'fashionBrand',
    legacyKeys: ['fashion-brand-name-generator'],
    navLabel: 'Fashion Brand Names',
    category: 'Fashion & Apparel',
    targetKeyword: 'fashion brand name generator',
    slug: '/fashion-brand-name-generator/',
    h1: 'Fashion Brand Name Generator',
    subtitle: 'Design runway-ready, editorial fashion brand names for haute couture houses, emerging fashion designers, resortwear labels, and global style brands.',
    defaultIndustryInput: 'Fashion',
    defaultKeywordsInput: 'couture, vogue, atelier, chic',
    defaultStyle: 'Creative',
    seoTitle: 'Fashion Brand Name Generator: 500+ Designer & Runway Ideas | BrandGenius',
    seoDescription: 'Generate chic, editorial fashion brand names for emerging designers, couture labels, and fashion startups. Instant domain checks & naming guide.',
    phoneticInsight: 'High-fashion naming favors architectural rhythm and European vowel resonance. Whether inspired by French ateliers or Scandinavian minimalism, great fashion names command visual authority in editorial mastheads.',
    marketPositioning: 'A fashion brand name is more than a clothing label—it is a cultural point of view that can span ready-to-wear, handbags, eyewear, fragrance, and runway shows.',
    frameworks: [
      {
        name: '01. Designer House & Atelier Titles',
        explanation: 'Positions your label with the gravity of a Paris, Milan, or London fashion house.',
        examples: ['Maison Veloce', 'Atelier Éclat', 'Studio Aurelia', 'Casa Sovrana', 'Haus of Valour', 'Atelier Noir']
      },
      {
        name: '02. Scandinavian & Minimalist Editorial Names',
        explanation: 'Clean, conceptual names with stark aesthetic discipline.',
        examples: ['Kinetix Studio', 'Forma Silhouette', 'Nordic Drape', 'Monolith Moda', 'Axis Couture', 'Aether Line']
      },
      {
        name: '03. Resortwear & Mediterranean Glamour Names',
        explanation: 'Sun-drenched, jet-set names for silk kaftans, swimwear, and linen tailoring.',
        examples: ['Solara Resort', 'Riviera Lumiere', 'Capri Velvet', 'Mirage Couture', 'Isla d’Or', 'Palazzo Sun']
      },
      {
        name: '04. Avant-Garde & Conceptual Runway Names',
        explanation: 'Provocative juxtapositions for boundary-pushing silhouettes and editorial collections.',
        examples: ['Velvet Riot', 'Silent Baroque', 'Gilded Void', 'Phantom Silk', 'Sublime Brutal', 'Nocturne Vogue']
      }
    ],
    curatedCategories: [
      {
        archetype: 'Haute Couture & Luxury Designer Brand Names',
        description: 'Prestigious names for eveningwear, bridal couture, and designer labels.',
        names: ['Maison Seraphina', 'Aurelia Couture', 'Velours Royal', 'Lumiere Paris', 'Sovereign Atelier', 'Veritas Moda', 'Élan d’Or', 'Celeste Runway', 'Vanguard Couture', 'Orchidée Studio', 'Noble Drape', 'Palazzo Chic']
      },
      {
        archetype: 'Contemporary Ready-to-Wear Fashion Names',
        description: 'Chic, boutique-ready names for modern seasonal collections and accessories.',
        names: ['VogueTheory', 'Muse & Silhouette', 'Solstice Moda', 'Aura Collective', 'Poise & Co.', 'Linen Atelier', 'VividRunway', 'ChicMeridian', 'VelvetLoom', 'HaloFashion', 'TrueVogue', 'LumaModa']
      }
    ],
    auditCriteria: [
      { dimension: 'Editorial Masthead Presence', detail: 'Fashion names must look striking in high-contrast serif or neo-grotesque display typography across lookbooks and campaign billboards.' },
      { dimension: 'Cross-Category Licensing Potential', detail: 'The most profitable fashion houses license their name to eyewear, fragrance, and leather goods—choose a name that transcends a single garment type.' }
    ],
    faqs: [
      {
        question: 'Should I name my fashion brand after myself or create a brand name?',
        answer: 'While founder names (like Tom Ford or Vera Wang) carry personal prestige, investors and acquirers today often prefer non-eponymous conceptual names (like Reformation, Zimmermann, or Khaite) because the brand equity is not tied to one individual.'
      },
      {
        question: 'How do I generate chic French or Italian-inspired fashion names?',
        answer: 'Use keywords like "atelier, maison, lumiere, veloce, eclat, or moda" in our Fashion Brand Name Generator above with the "Classic" or "Creative" tone selected.'
      }
    ]
  },

  // ==========================================
  // 15. PERFUME BRAND NAME GENERATOR
  // ==========================================
  {
    key: 'perfumeBrand',
    legacyKeys: ['perfume-brand-name-generator'],
    navLabel: 'Perfume & Fragrance Names',
    category: 'Luxury, Jewelry & Lifestyle',
    targetKeyword: 'perfume brand name generator',
    slug: '/perfume-brand-name-generator/',
    h1: 'Perfume & Fragrance Brand Name Generator',
    subtitle: 'Compose evocative, olfactory brand names for niche parfum houses, luxury extrait de parfum lines, botanical body mists, and bespoke scent ateliers.',
    defaultIndustryInput: 'Perfume',
    defaultKeywordsInput: 'sillage, oud, botanica, accord',
    defaultStyle: 'Classic',
    seoTitle: 'Perfume Brand Name Generator: 500+ Fragrance & Scent Ideas | BrandGenius',
    seoDescription: 'Generate luxurious, poetic perfume and fragrance brand names for niche parfumeries, colognes, and scent brands. Instant domain check & guide.',
    phoneticInsight: 'Olfactory naming relies on sibilants (S, Sh, Z) and nasal/liquid sonorants (M, N, L, R) that mimic the diffusion of vapor and lingering sillage in the air.',
    marketPositioning: 'Because consumers cannot smell a perfume through a screen, your fragrance brand name and bottle typography must do 100% of the sensory storytelling online—translating invisible notes of bergamot, oud, amber, and iris into emotion.',
    frameworks: [
      {
        name: '01. Niche Parfumerie & Apothecary Lab Names',
        explanation: 'Inspired by Grasse perfumeries, numbered accords, and artisanal apothecary flacons.',
        examples: ['Atelier Sillage', 'Maison Accord', 'Laboratoire Oud', 'Botanica No. 08', 'Vial & Vapor', 'Parfum Veritas']
      },
      {
        name: '02. Poetic, Nocturnal & Atmospheric Names',
        explanation: 'Captures memory, twilight, rain, and intoxication for Moody, unisex niche scents.',
        examples: ['Nocturne Mist', 'Velvet Sillage', 'Phantom Amber', 'Shadow & Iris', 'Elysian Accord', 'Midnight Vetiver']
      },
      {
        name: '03. Clean, Skin-Scent & Botanical Fragrance Names',
        explanation: 'Phthalate-free, molecular, and sunlit names for everyday skin scents and hair mists.',
        examples: ['SecondSkin Parfum', 'Dew & Cedar', 'Sunlit Neroli', 'PureSillage', 'CloudMusk', 'FloraMist']
      },
      {
        name: '04. Opulent Oud & Royal Oriental Parfum Names',
        explanation: 'Rich, resinous, gold-crested names for extrait de parfum and Middle Eastern luxury scents.',
        examples: ['Sovereign Oud', 'Royal Ambergris', 'Gilded Saffron', 'Palazzo Parfum', 'Imperial Myrrh', 'Crown of Grasse']
      }
    ],
    curatedCategories: [
      {
        archetype: 'Niche & Artisanal Perfume House Names',
        description: 'Sophisticated names for unisex eau de parfum and discovery set brands.',
        names: ['Maison Sillage', 'Aether Parfums', 'Lumina Accord', 'Veritas Olfactive', 'Atelier Vetiver', 'Solstice Parfum', 'Noir Botanica', 'Valour Fragrance', 'Éclat de Nuit', 'Alchemy Scent Co.', 'Vanguard Parfum', 'Orchid & Oud']
      },
      {
        archetype: 'Clean, Molecular & Everyday Fragrance Names',
        description: 'Fresh, modern names for clean perfumes, rollerballs, and body mists.',
        names: ['SkinTheory Scent', 'AuraMist', 'PureAccord', 'HaloParfum', 'Petal & Musk', 'CloudSillage', 'Daybreak Scent', 'Linen & Bergamot', 'Lucid Fragrance', 'Verdant Vial', 'BreezeAccord', 'SoftNote Lab']
      }
    ],
    auditCriteria: [
      { dimension: 'Flacon Label Proportions', detail: 'Niche perfume labels follow classic rectangular apothecary grids—names with clean typographic hierarchy elevate perceived bottle value from $40 to $240.' },
      { dimension: 'Olfactory Synesthesia', detail: 'Match hard or soft consonants to your scent family (soft liquids for florals/musks; rich velars and labials for woods/ouds).' }
    ],
    faqs: [
      {
        question: 'What makes a perfume brand name sound luxurious?',
        answer: 'Niche fragrance houses (like Byredo, Le Labo, Diptyque, and Parfums de Marly) use French, Latin, or enigmatic coined words that suggest artistry, memory, and alchemy rather than literal fruit or flower names.'
      },
      {
        question: 'Can I use this generator for individual scent or perfume bottle names?',
        answer: 'Yes! You can use our Perfume Brand Name Generator both to name your overarching fragrance house AND to brainstorm poetic names for individual eau de parfum releases.'
      }
    ]
  },

  // ==========================================
  // 16. HAIRCARE BRAND NAME GENERATOR
  // ==========================================
  {
    key: 'haircareBrand',
    legacyKeys: ['haircare-brand-name-generator'],
    navLabel: 'Haircare Brand Names',
    category: 'Beauty & Cosmetics',
    targetKeyword: 'haircare brand name generator',
    slug: '/haircare-brand-name-generator/',
    h1: 'Haircare Brand Name Generator',
    subtitle: 'Create salon-grade, bond-building, and botanical haircare brand names for shampoos, scalp serums, curl care lines, and luxury hair oils.',
    defaultIndustryInput: 'Haircare',
    defaultKeywordsInput: 'strand, Mane, follicle, silk',
    defaultStyle: 'Modern',
    seoTitle: 'Haircare Brand Name Generator: 500+ Salon & Hair Brand Ideas | BrandGenius',
    seoDescription: 'Generate professional haircare brand names for salon lines, curl brands, scalp treatments, and luxury hair oils. Instant domain checks & SEO guide.',
    phoneticInsight: 'Haircare naming blends "silky" fricatives (S, Sh, V, L) that evoke shine, slip, and smoothness with structural roots (Bond, Root, Crown, Strand) that promise strength and volume.',
    marketPositioning: 'The "skinification of hair" has transformed haircare into a high-margin prestige category. Whether you formulate peptide scalp serums, textured curl creams, or bond-repair masks, your name must command authority in the shower and on the salon backbar.',
    frameworks: [
      {
        name: '01. Bond-Repair & Scalp Science Names',
        explanation: 'Clinical, molecular names for keratin repair, hair growth peptides, and microbiome scalp care.',
        examples: ['BondCore Lab', 'FollicleIQ', 'ScalpBiome', 'KeratinPulse', 'StrandLogic', 'RootMatrix']
      },
      {
        name: '02. Luxury Salon & Editorial Hair Atelier Names',
        explanation: 'Prestige names inspired by celebrity session stylists and blowout bars.',
        examples: ['Maison Mane', 'Atelier Silk', 'Sovereign Strand', 'Lumiere Hair', 'Velvet Tress', 'Crown & Gloss']
      },
      {
        name: '03. Textured, Curl & Coil Celebration Names',
        explanation: 'Warm, nourishing, community-first names for curls, coils, braids, and protective styles.',
        examples: ['CoilSanctuary', 'CrownRitual', 'NectarCurl', 'Bounce & Bloom', 'HaloCoils', 'Velour Curls']
      },
      {
        name: '04. Botanical Hair Oils & Clean Shower Rituals',
        explanation: 'Infused with rosemary, argan, honey, and botanicals for holistic hair wellness.',
        examples: ['Botanica Tress', 'HoneyRoot Co.', 'Verdant Strand', 'FloraMane', 'PureElixir Hair', 'Solstice Locks']
      }
    ],
    curatedCategories: [
      {
        archetype: 'Prestige Salon & Bond-Building Haircare Names',
        description: 'High-performance names for professional shampoos, masks, and treatments.',
        names: ['SilkBond Lab', 'LuminaMane', 'ApexStrand', 'Veritas Hair', 'CrownTheory', 'VelvetFollicle', 'OptiTress', 'GlossAtelier', 'PureKeratin Co.', 'Vanguard Salon', 'AuraLocks', 'Mirage Haircare']
      },
      {
        archetype: 'Curl, Scalp & Natural Hair Oil Brand Names',
        description: 'Nourishing names for growth oils, curl definers, and clean scalp rituals.',
        names: ['RootedCrown', 'DewCoil', 'Rosemary & Mane', 'NectarStrand', 'BloomScalp', 'TerraTress', 'GoldenElixir Hair', 'Sanctuary Curls', 'Canopy Hair Co.', 'Solara Strands', 'Kindred Mane', 'LushRoot']
      }
    ],
    auditCriteria: [
      { dimension: 'Vertical Shower Bottle Readability', detail: '8oz and 10oz shampoo bottles are tall cylinders—short horizontal names or clean vertical-stack typography work best.' },
      { dimension: 'Salon Backbar Authority', detail: 'Stylists recommend brands that sound credible and professional—avoid overly juvenile puns.' }
    ],
    faqs: [
      {
        question: 'What makes a haircare brand name stand out on retail shelves?',
        answer: 'Leading haircare brands (like Olaplex, Ouai, Briogeo, Gisou, and K18) use short, distinctive coined or rhythmic names that look bold on minimalist bottles and convey either molecular science or effortless French/botanical luxury.'
      },
      {
        question: 'Can I use this generator for a hair salon or barbershop name?',
        answer: 'Yes! Enter keywords like "salon, studio, crown, shear, or collective" into the form above to generate elevated salon and barbershop brand names.'
      }
    ]
  },

  // ==========================================
  // 17. CANDLE BRAND NAME GENERATOR
  // ==========================================
  {
    key: 'candleBrand',
    legacyKeys: ['candle-brand-name-generator'],
    navLabel: 'Candle Brand Names',
    category: 'Luxury, Jewelry & Lifestyle',
    targetKeyword: 'candle brand name generator',
    slug: '/candle-brand-name-generator/',
    h1: 'Candle Brand Name Generator',
    subtitle: 'Illuminate your home fragrance line with atmospheric, luxury candle brand names crafted for soy-wax vessels, apothecary jars, and reed diffusers.',
    defaultIndustryInput: 'Candle',
    defaultKeywordsInput: 'ember, hearth, lumen, vessel',
    defaultStyle: 'Classic',
    seoTitle: 'Candle Brand Name Generator: 500+ Luxury Home Fragrance Ideas | BrandGenius',
    seoDescription: 'Generate warm, evocative candle brand names for luxury soy candles, home fragrance lines, and wax melt businesses. Instant domain checks & guide.',
    phoneticInsight: 'Candle and home fragrance names thrive on warm, glowing sonorants (L, M, N, W, H) and words associated with light, twilight, warmth, and architectural sanctuary.',
    marketPositioning: 'A luxury candle is an interior design object first and a scent product second. Because customers leave candle jars out on marble coffee tables and bookshelves long after the wax burns down, your candle brand name must look like an art gallery label.',
    frameworks: [
      {
        name: '01. Architectural & Luxury Vessel Names',
        explanation: 'Positions candles as collectible home decor objects and fragrance sculptures.',
        examples: ['Maison Lumen', 'Atelier Ember', 'Vessel & Wick', 'Sovereign Flame', 'Palazzo Wax', 'Aura Sanctuary']
      },
      {
        name: '02. Cozy Hearth, Woodland & Seasonal Nostalgia Names',
        explanation: 'Evokes crackling fireplaces, cabin retreats, rain on cedar, and autumn warmth.',
        examples: ['Hearth & Hollow', 'EmberGlow Co.', 'Kindred Flame', 'Timber & Wax', 'Solstice Hearth', 'Willow Wick']
      },
      {
        name: '03. Apothecary & Botanical Soy Candle Names',
        explanation: 'Clean-burning coconut/soy wax, essential oils, and amber glass jars.',
        examples: ['Botanica Lumen', 'Verdant Vessel', 'Flora & Flame', 'PureWick Lab', 'Herbarium Candle', 'TerraGlow']
      },
      {
        name: '04. Moody, Nocturnal & Mystic Candle Names',
        explanation: 'For gothic, celestial, crystal-infused, or late-night lounge home fragrances.',
        examples: ['Nocturne Wax', 'Eclipse & Ember', 'Shadow Vessel', 'Moonlit Wick', 'Phantom Flame', 'Vespera Candle']
      }
    ],
    curatedCategories: [
      {
        archetype: 'Luxury & Designer Home Fragrance Brand Names',
        description: 'Sophisticated names inspired by Diptyque, Byredo, and Trudon.',
        names: ['Maison Luma', 'Atelier Sillage', 'Lumiere & Wax', 'Veritas Vessel', 'Sovereign Hearth', 'Éclat Candle Co.', 'Aether & Flame', 'Valour Home Scent', 'Orchid & Ember', 'NobleWick', 'Cire Royale', 'Sanctum Fragrance']
      },
      {
        archetype: 'Artisanal Hand-Poured & Eco Soy Candle Names',
        description: 'Warm, giftable names for small-batch soy and coconut wax candles.',
        names: ['GoldenHour Wick', 'Hearthstone Soy', 'Meadow & Flame', 'PureVessel Co.', 'Sunlit Wax', 'Haven & Hearth', 'Botanic Glow', 'QuietEmber', 'Linen & Lumen', 'Honeycomb Wax', 'Solace Candle', 'KindFlame']
      }
    ],
    auditCriteria: [
      { dimension: 'Jar Label Grid Aesthetics', detail: 'Luxury candle labels use structured typographic boxes—your brand name should sit cleanly at the top or bottom of a square label alongside scent notes.' },
      { dimension: 'Home Fragrance Expansion', detail: 'Avoid using "Wax" in your core name if you plan to expand into room sprays, reed diffusers, and incense.' }
    ],
    faqs: [
      {
        question: 'How do I name a luxury candle business?',
        answer: 'Focus on atmosphere, light, and architectural calm rather than generic craft-fair names. Words like Lumen, Vessel, Hearth, Ember, Solstice, Atelier, and Sanctuary create an immediate luxury impression.'
      },
      {
        question: 'What trademark class covers candles and home fragrances?',
        answer: 'Candles and wicks fall under USPTO International Class 004, while room sprays and reed diffusers fall under Class 003. Choosing a distinctive coined or compound name helps protect your brand across both classes.'
      }
    ]
  },

  // ==========================================
  // 18. WATCH BRAND NAME GENERATOR
  // ==========================================
  {
    key: 'watchBrand',
    legacyKeys: ['watch-brand-name-generator'],
    navLabel: 'Watch Brand Names',
    category: 'Luxury, Jewelry & Lifestyle',
    targetKeyword: 'watch brand name generator',
    slug: '/watch-brand-name-generator/',
    h1: 'Watch Brand Name Generator',
    subtitle: 'Engineer timeless, precision watch brand names for microbrand horology, Swiss-inspired automatic timepieces, dive watches, and minimalist dress watches.',
    defaultIndustryInput: 'Watch',
    defaultKeywordsInput: 'chrono, caliber, meridian, horology',
    defaultStyle: 'Classic',
    seoTitle: 'Watch Brand Name Generator: 500+ Horology & Timepiece Ideas | BrandGenius',
    seoDescription: 'Generate prestigious watch brand names for timepiece microbrands, luxury watches, and chronographs. Free domain checks & horology naming guide.',
    phoneticInsight: 'Horological naming demands compact visual symmetry and crisp, authoritative consonants (C, K, V, T, R, X). On a watch dial, the brand name sits below the 12 o’clock index in less than 12 millimeters of width.',
    marketPositioning: 'In the golden era of independent watch microbrands and luxury timepieces, collectors scrutinize dial typography more fiercely than in any other industry. A clumsy name on a dial will kill a watch launch regardless of the movement inside.',
    frameworks: [
      {
        name: '01. Swiss & European Horological Heritage Names',
        explanation: 'Evokes Geneva ateliers, mechanical calibers, and generational watchmaking.',
        examples: ['Vane & Genève', 'Laurent Horology', 'Montclair Chrono', 'Atelier Caliber', 'Sovereign Time', 'Veritas Watch Co.']
      },
      {
        name: '02. Precision Navigation, Aviation & Dive Watch Names',
        explanation: 'Built for 300m dive watches, GMT pilot watches, and expedition field instruments.',
        examples: ['Meridian Instruments', 'Apex Chrono', 'Vanguard GMT', 'Nautilus Caliber', 'Zenithal Watch', 'ChronoVector']
      },
      {
        name: '03. Minimalist Bauhaus & Contemporary Dress Watches',
        explanation: 'Clean, architectural names for ultra-thin dress watches and modern design timepieces.',
        examples: ['Aether Time', 'Forma Horology', 'LumaDial', 'MonoCaliber', 'Axis Timepieces', 'Tempo Studio']
      },
      {
        name: '04. Short 5-to-7 Letter Symmetric Dial Names',
        explanation: 'Engineered specifically for balanced kerning beneath the 12 o’clock marker.',
        examples: ['Valour', 'Kronos', 'Veloce', 'Aureon', 'Solara', 'Vervex']
      }
    ],
    curatedCategories: [
      {
        archetype: 'Luxury & Heritage Watch Microbrand Names',
        description: 'Prestigious names for mechanical, automatic, and dress timepieces.',
        names: ['Sovereign Horology', 'Aurelia Genève', 'Valour Watch Co.', 'Meridian Timepieces', 'Veritas Caliber', 'Crown & Escapement', 'Palazzo Chrono', 'Noble Tourbillon', 'Sterling Horology', 'Vanguard Watch', 'Epoch Timepieces', 'Pinnacle Dial']
      },
      {
        archetype: 'Tool, Dive & Field Watch Brand Names',
        description: 'Rugged, instrument-grade names for sports, aviation, and tactical watches.',
        names: ['ChronoApex', 'Abyssal Instruments', 'Vector Watch Co.', 'TitanCaliber', 'Horizon GMT', 'IronBezel', 'Kinetix Chrono', 'Summit Field Watch', 'Polaris Time', 'Velocity Horology', 'Bastion Watch', 'Sector Chrono']
      }
    ],
    auditCriteria: [
      { dimension: '12 O’Clock Dial Symmetry', detail: 'Watch enthusiasts obsess over dial balance. Names with 5–8 letters and symmetrical outer characters (like ROLEX, OMEGA, TUDOR, SEIKO) center effortlessly below the 12 o’clock baton.' },
      { dimension: 'Crown & Caseback Engraving', detail: 'Your name or initial emblem must engrave cleanly onto a 6mm screw-down crown.' }
    ],
    faqs: [
      {
        question: 'Why is naming a watch microbrand so challenging?',
        answer: 'Watch enthusiasts frequently reject microbrands solely because the name on the dial looks too long, uses cheap-looking fonts, or sounds like a dropshipped fashion watch. Stick to 1-word Latin/European roots, respected surnames, or precision navigation terms (like Meridian, Epoch, or Valour).'
      },
      {
        question: 'What trademark class applies to watches?',
        answer: 'Watches, chronographs, and horological instruments fall under USPTO International Class 14 (alongside jewelry). Always verify domain and trademark availability early.'
      }
    ]
  },

  // ==========================================
  // 19. ACTIVEWEAR BRAND NAME GENERATOR
  // ==========================================
  {
    key: 'activewearBrand',
    legacyKeys: ['activewear-brand-name-generator'],
    navLabel: 'Activewear Brand Names',
    category: 'Fitness, Health & Wellness',
    targetKeyword: 'activewear brand name generator',
    slug: '/activewear-brand-name-generator/',
    h1: 'Activewear & Athleisure Brand Name Generator',
    subtitle: 'Create sleek, high-stretch activewear brand names for yoga leggings, seamless gym sets, running apparel, and elevated athleisure labels.',
    defaultIndustryInput: 'Activewear',
    defaultKeywordsInput: 'sculpt, flow, kinetic, stride',
    defaultStyle: 'Modern',
    seoTitle: 'Activewear Brand Name Generator: 500+ Athleisure & Gymwear Ideas | BrandGenius',
    seoDescription: 'Generate modern activewear and athleisure brand names for gym clothing, yoga wear, and performance apparel. Instant domain check & naming guide.',
    phoneticInsight: 'Activewear blends athletic velocity (V, K, Z, P) with second-skin softness (L, M, S, F)—reflecting garments that transition seamlessly from high-intensity training to coffee runs.',
    marketPositioning: 'Following the blueprint of Lululemon, Alo Yoga, Vuori, and Gymshark, modern activewear names must work as lifestyle badges on waistbands and ankle cuffs.',
    frameworks: [
      {
        name: '01. Second-Skin Sculpt & Yoga Flow Names',
        explanation: 'Soft, anatomical, fluid names for buttery-soft leggings, sports bras, and Pilates sets.',
        examples: ['SculptLuma', 'AuraFlow', 'VelvetStride', 'SecondSkin Active', 'Poise & Pulse', 'SomaActive']
      },
      {
        name: '02. High-Output Training & Gym Apparel Names',
        explanation: 'Dynamic, technical names for compression gear, lifting shorts, and marathon singlets.',
        examples: ['Kinetix Wear', 'VeloStride', 'ApexAthletica', 'PulseFit Apparel', 'AeroCore', 'EndureLoom']
      },
      {
        name: '03. Coastal & Everyday Athleisure Lifestyle Names',
        explanation: 'Breezy, versatile names for joggers, performance polos, and travel-ready comfort.',
        examples: ['Solara Active', 'Coastline Pace', 'DriftAthleisure', 'Meridian Move', 'Canyon & Core', 'Daybreak Active']
      }
    ],
    curatedCategories: [
      {
        archetype: 'Yoga, Pilates & Studio Athleisure Names',
        description: 'Elevated names for women’s studio sets, loungewear, and movement apparel.',
        names: ['Lumina Yoga', 'SculptTheory', 'AuraAthletica', 'SilkStride', 'Solstice Move', 'Forma Active', 'CloudLoom Fit', 'Velour Motion', 'HaloActive', 'Breathe & Co.', 'PureCadence', 'Zenith Flow']
      },
      {
        archetype: 'Performance Gym & Running Activewear Names',
        description: 'High-energy names for technical training gear and endurance apparel.',
        names: ['Veloce Active', 'KineticThread', 'ApexMotion', 'VoltApparel', 'StrideForge', 'PulseAthletica', 'HyperFlex Wear', 'TempoCore', 'SummitActive', 'TrackLoom', 'IgniteWear', 'VertexFit']
      }
    ],
    auditCriteria: [
      { dimension: 'Waistband & Ankle Reflective Print', detail: 'Activewear logos are heat-transferred onto high-stretch elastane fabrics—short names or geometric symbols prevent cracking and distortion when stretched.' },
      { dimension: 'Studio-to-Street Versatility', detail: 'Names that sound too hardcore alienate everyday athleisure buyers; balanced phonetic roots expand your total addressable market.' }
    ],
    faqs: [
      {
        question: 'What makes an activewear brand name successful like Alo or Vuori?',
        answer: 'Short, vowel-rich, 2-to-3 syllable words (Alo, Vuori, Set, Tala, Rhone) feel effortless, modern, and gender-versatile while allowing a clean minimalist monogram on clothing.'
      },
      {
        question: 'How do I generate activewear names for a specific sport like tennis or golf?',
        answer: 'Enter club-inspired keywords like "court, club, rally, green, or crest" into the Keywords field above and select "Classic" or "Modern" to generate country-club and court-ready athleisure names.'
      }
    ]
  },

  // ==========================================
  // 20. SUPPLEMENT BRAND NAME GENERATOR
  // ==========================================
  {
    key: 'supplementBrand',
    legacyKeys: ['supplement-brand-name-generator'],
    navLabel: 'Supplement Brand Names',
    category: 'Fitness, Health & Wellness',
    targetKeyword: 'supplement brand name generator',
    slug: '/supplement-brand-name-generator/',
    h1: 'Supplement & Wellness Brand Name Generator',
    subtitle: 'Formulate high-trust, bioavailable brand names for longevity supplements, nootropics, clean protein powders, electrolytes, and daily greens.',
    defaultIndustryInput: 'Supplement',
    defaultKeywordsInput: 'vital, bio, longevity, cellular',
    defaultStyle: 'Modern',
    seoTitle: 'Supplement Brand Name Generator: 500+ Nutrition & Vitamin Ideas | BrandGenius',
    seoDescription: 'Generate scientific, clean supplement brand names for vitamins, protein powders, nootropics, and longevity brands. Instant domain checks & guide.',
    phoneticInsight: 'Supplement naming balances clinical authority (Bio, Cell, Opti, Syn, Lab) with vitality and clarity (Luma, Vital, Pure, Thrive), avoiding outdated "bodybuilder hype" in favor of longevity and scientific trust.',
    marketPositioning: 'Whether competing with AG1 in daily greens, Thorne in clinical longevity, or Liquid I.V. in hydration, your supplement brand name must signal third-party-tested purity and measurable human optimization.',
    frameworks: [
      {
        name: '01. Cellular Longevity & Bio-Optimization Names',
        explanation: 'Clinical, research-backed nomenclature for NAD+ boosters, nootropics, and daily foundational stacks.',
        examples: ['CelluVital', 'BioOptima', 'Longevity Lab', 'SynapseCore', 'ChronoCell', 'Aether Biosciences']
      },
      {
        name: '02. Clean Daily Ritual Greens, Electrolytes & Gummies',
        explanation: 'Friendly, habit-forming names designed for kitchen countertops and daily subscriptions.',
        examples: ['DailyThrive', 'PureHydrate', 'LumaGreens', 'MorningRitual Co.', 'VitalDrop', 'BloomNutrition']
      },
      {
        name: '03. Athletic Performance Protein & Pre-Workout Names',
        explanation: 'High-output, clean-label names for whey isolate, creatine, and endurance fuel.',
        examples: ['ApexFuel', 'KineticNutrition', 'VoltPerform', 'PrimeIsolate', 'EndureLabs', 'TitanBio']
      }
    ],
    curatedCategories: [
      {
        archetype: 'Longevity, Nootropic & Clinical Vitamin Names',
        description: 'Doctor-trusted names for cognitive, sleep, and cellular health supplements.',
        names: ['OptiCell Labs', 'Veritas Bio', 'NeuroLuma', 'Synergy Vital', 'Pinnacle Longevity', 'BioHorizon', 'CoreElement', 'Clarity Nootropics', 'Zenith Biosciences', 'PureProtocol', 'Vanguard Health', 'NovaCellular']
      },
      {
        archetype: 'Sports Nutrition, Hydration & Protein Brand Names',
        description: 'Clean, energetic names for electrolytes, pre-workouts, and protein powders.',
        names: ['HydroPulse', 'Kinetix Fuel', 'ApexElectrolyte', 'StrideNutrition', 'VeloProtein', 'PurePeak Labs', 'IgniteBio', 'FlexNutrition', 'SummitHydrate', 'UltraCore Fuel', 'TempoLabs', 'PrimeVital']
      }
    ],
    auditCriteria: [
      { dimension: 'FDA Structure/Function Claim Safety', detail: 'In Class 005 (Dietary Supplements), never include disease names or implied medical cures in your brand name.' },
      { dimension: 'Tub & Sachet Readability', detail: 'Single-serve electrolyte stick packs are only 2cm wide—short, high-contrast names maximize readability.' }
    ],
    faqs: [
      {
        question: 'What makes a supplement brand name trustworthy?',
        answer: 'Modern supplement buyers avoid exaggerated "extreme" names and gravitate toward clean, transparent names that reference cellular biology, daily rituals, or elemental purity (like Ritual, Thorne, Momentous, and Seed).'
      },
      {
        question: 'What trademark class is used for supplements and vitamins?',
        answer: 'Dietary and nutritional supplements, protein powders, and vitamins are registered under USPTO International Class 005.'
      }
    ]
  },

  // ==========================================
  // 21. FOOD BRAND NAME GENERATOR
  // ==========================================
  {
    key: 'foodBrand',
    legacyKeys: ['food-brand-name-generator'],
    navLabel: 'Food & CPG Brand Names',
    category: 'Food, Beverage & Spirits',
    targetKeyword: 'food brand name generator',
    slug: '/food-brand-name-generator/',
    h1: 'Food & CPG Brand Name Generator',
    subtitle: 'Create craveable, shelf-popping food brand names for modern CPG startups, healthy snacks, artisanal pantry staples, hot sauces, and plant-based foods.',
    defaultIndustryInput: 'Food',
    defaultKeywordsInput: 'harvest, crisp, flavor, organic',
    defaultStyle: 'Creative',
    seoTitle: 'Food Brand Name Generator: 500+ CPG, Snack & Pantry Ideas | BrandGenius',
    seoDescription: 'Generate delicious, memorable food and CPG brand names for snacks, sauces, organic foods, and beverage startups. Free domain check & guide.',
    phoneticInsight: 'Food and CPG naming thrives on onomatopoeia, alliteration, and gustatory consonants (C, K, P, B, M) that mimic crunch, zest, and indulgence.',
    marketPositioning: 'In grocery aisles (Whole Foods, Erewhon, Target), a shopper’s eyes scan past 300 SKUs per minute. Disruptive CPG brands win with bold, human, category-breaking names that spark instant curiosity.',
    frameworks: [
      {
        name: '01. Disruptive Modern CPG & Better-For-You Snacks',
        explanation: 'Playful, punchy names that turn everyday pantry items into cult lifestyle brands.',
        examples: ['CrunchTheory', 'ZestPop', 'GoodCrumb', 'BoldBite Co.', 'CrispyHabit', 'VividSnack']
      },
      {
        name: '02. Artisanal Pantry, Olive Oil & Gourmet Condiments',
        explanation: 'Sun-drenched, culinary-forward names for chili crisp, infused honey, pasta, and olive oil.',
        examples: ['Solara Groves', 'Hearth & Harvest', 'CasaSavor', 'Gilded Spoon', 'TerraPantry', 'Veritas Table']
      },
      {
        name: '03. Plant-Based, Organic & Regenerative Food Names',
        explanation: 'Clean, earth-honoring names for alt-dairy, sprouted grains, and organic produce.',
        examples: ['Verdant Kitchen', 'RootedTable', 'PureCanopy', 'Sprout & Co.', 'MeadowHarvest', 'BiomeFoods']
      }
    ],
    curatedCategories: [
      {
        archetype: 'Better-For-You Snack & Functional Food Names',
        description: 'High-energy names for protein bars, puffs, chips, and sweet treats.',
        names: ['CrispPulse', 'JoyCrumb', 'ZestBite', 'SunPop Snacks', 'PureCrunch Co.', 'VibePantry', 'GlowBite', 'NectarBar', 'HaloSweets', 'KineticSnack', 'CloudPuff', 'TrueTreat']
      },
      {
        archetype: 'Artisanal Sauces, Spices & Gourmet Pantry Names',
        description: 'Elevated culinary names for hot sauces, olive oils, and specialty foods.',
        names: ['Ember & Spice', 'Solstice Table', 'Gilded Harvest', 'CopperKettle Provisions', 'NobleGroves', 'Sovereign Pantry', 'Mesa Fuego', 'Orchard & Cask', 'Velvet Spoon', 'Aurelia Olive Co.', 'Kindred Kitchen', 'Heritage Crumb']
      }
    ],
    auditCriteria: [
      { dimension: '3-Second Grocery Shelf Pop', detail: 'CPG food names must read in a single glance at 40pt+ font size on front-of-pack pouches and boxes.' },
      { dimension: 'Appetite Appeal Phonetics', detail: 'Avoid harsh clinical or chemical syllables in food names; favor warm vowels and crisp plosives.' }
    ],
    faqs: [
      {
        question: 'What makes a modern CPG food brand name go viral?',
        answer: 'Modern CPG winners (like Graza, Fly By Jing, Momofuku, Olipop, and Magic Spoon) combine unexpected brevity with playful or culinary heritage imagery that stands out against legacy grocery conglomerates.'
      },
      {
        question: 'Can I check .com domains for my food startup here?',
        answer: 'Yes, every generated food brand name includes a direct link to verify domain availability.'
      }
    ]
  },

  // ==========================================
  // 22. RESTAURANT BRAND NAME GENERATOR
  // ==========================================
  {
    key: 'restaurantBrand',
    legacyKeys: ['restaurant-brand-name-generator'],
    navLabel: 'Restaurant Brand Names',
    category: 'Food, Beverage & Spirits',
    targetKeyword: 'restaurant brand name generator',
    slug: '/restaurant-brand-name-generator/',
    h1: 'Restaurant & Hospitality Brand Name Generator',
    subtitle: 'Craft atmospheric, reservation-driving restaurant brand names for fine dining concepts, fast-casual chains, ghost kitchens, bistros, and rooftop lounges.',
    defaultIndustryInput: 'Restaurant',
    defaultKeywordsInput: 'bistro, hearth, table, ember',
    defaultStyle: 'Classic',
    seoTitle: 'Restaurant Brand Name Generator: 500+ Dining & Bistro Ideas | BrandGenius',
    seoDescription: 'Generate unforgettable restaurant, bistro, fast-casual, and bar names. Check domain availability and explore our hospitality naming guide.',
    phoneticInsight: 'Hospitality naming relies on acoustic warmth and cadence—evoking the clink of glassware, open-fire hearths, and memorable dining destinations.',
    marketPositioning: 'Whether diners discover you on Resy, OpenTable, Google Maps, or UberEats, your restaurant name sets expectations for cuisine, ambiance, and check average before they ever read the menu.',
    frameworks: [
      {
        name: '01. Fire, Hearth & Elemental Fine Dining Names',
        explanation: 'Evokes wood-fired ovens, seasonal tasting menus, and chef-driven culinary theater.',
        examples: ['Ember & Ash', 'Hearthstone Table', 'Solstice Kitchen', 'Alder & Iron', 'Lumina Dining', 'Veritas Bistro']
      },
      {
        name: '02. Fast-Casual & Scalable Concept Names',
        explanation: 'Punchy, repeatable names built for multi-unit expansion and delivery app visibility.',
        examples: ['BowlTheory', 'Crisp & Co.', 'GreenPulse Kitchen', 'FreshCraft', 'VeloBite', 'DailyGrain']
      },
      {
        name: '03. European Bistro, Trattoria & Brasserie Names',
        explanation: 'Romantic, neighborhood-anchoring names for wine bars, pasta concepts, and brasseries.',
        examples: ['Maison Dorée', 'Osteria Solis', 'Palazzo Table', 'Café Lumiere', 'Bistro Sovrano', 'Casa Verde']
      }
    ],
    curatedCategories: [
      {
        archetype: 'Upscale Dining, Bistro & Wine Bar Names',
        description: 'Sophisticated names for date-night restaurants, chef tables, and cocktail lounges.',
        names: ['The Gilded Fig', 'Hearth & Vine', 'Maison Ember', 'Velvet Table', 'Aurelia Brasserie', 'Nocturne Lounge', 'Sovereign Kitchen', 'Copper & Thyme', 'Solara Bistro', 'Lumen Osteria', 'Willow & Wine', 'Sanctuary Dining']
      },
      {
        archetype: 'Fast-Casual, Street Food & Ghost Kitchen Names',
        description: 'High-energy, craveable names for modern casual concepts and food trucks.',
        names: ['CrunchSyndicate', 'FlameCraft Kitchen', 'Zest & Bowl', 'UrbanSkillet', 'PulseTaco', 'SmashTheory', 'VividPlate', 'NextDoor Kitchen', 'HotCrumb', 'Nomad Eats', 'ShiftBurger', 'CoreKitchen']
      }
    ],
    auditCriteria: [
      { dimension: 'Local SEO & Voice Search Clarity', detail: 'Diners frequently search "restaurants near me" or ask Siri/Google Maps for directions—avoid names that are impossible to spell or identical to nearby streets.' },
      { dimension: 'Signage & Menu Cover Balance', detail: 'Ensure the name translates effortlessly onto exterior neon/brass blade signs and linen menu covers.' }
    ],
    faqs: [
      {
        question: 'How do I choose a restaurant name that attracts reservations?',
        answer: 'Avoid generic geographic + cuisine formulas (like "Main Street Italian"). Instead, choose a name that captures a sensory element, botanical ingredient, or architectural mood (like Carbone, Balthazar, Nobu, or Ember & Vine).'
      },
      {
        question: 'What trademark class covers restaurants and bars?',
        answer: 'Restaurant, bar, café, and catering services fall under USPTO International Class 43.'
      }
    ]
  },

  // ==========================================
  // 23. TECH BRAND NAME GENERATOR
  // ==========================================
  {
    key: 'techBrand',
    legacyKeys: ['tech-brand-name-generator'],
    navLabel: 'Tech & SaaS Brand Names',
    category: 'Tech, Auto & Commerce',
    targetKeyword: 'tech brand name generator',
    slug: '/tech-brand-name-generator/',
    h1: 'Tech & SaaS Brand Name Generator',
    subtitle: 'Architect scalable, category-defining tech brand names for B2B SaaS platforms, fintech startups, cybersecurity firms, developer tools, and hardware innovators.',
    defaultIndustryInput: 'Technology',
    defaultKeywordsInput: 'cloud, sync, matrix, vector',
    defaultStyle: 'Modern',
    seoTitle: 'Tech Brand Name Generator: 500+ SaaS & Startup Name Ideas | BrandGenius',
    seoDescription: 'Generate crisp, scalable tech and SaaS startup names with instant domain availability checks and deterministic brand auditing.',
    phoneticInsight: 'Tech and SaaS nomenclature favors high-velocity, low-friction phonetics (Stripe, Linear, Vercel, Notion, Figma, Datadog)—short 2-syllable words with crisp plosive or sibilant boundaries that sound effortless in investor pitches and enterprise boardrooms.',
    marketPositioning: 'A great technology brand name avoids dated ".io" gimmicks or vowel-dropping clichés that confuse enterprise buyers. It projects reliability, speed, and architectural clarity.',
    frameworks: [
      {
        name: '01. Geometric, Physics & Infrastructure Names',
        explanation: 'Signals foundational stability, speed, and mathematical precision.',
        examples: ['VectorCore', 'Vertex Cloud', 'Axis Protocol', 'LinearSync', 'QuantumGrid', 'PrismOS']
      },
      {
        name: '02. Action-Oriented Workflow & SaaS Names',
        explanation: 'Communicates seamless collaboration, automation, and operational velocity.',
        examples: ['FlowStack', 'ShiftLogic', 'PulseMetrics', 'SyncBase', 'ElevateHQ', 'StrideOps']
      },
      {
        name: '03. Coined Enterprise Neologisms',
        explanation: 'Clean 5-to-7 letter invented words with high global Class 09 and Class 42 availability.',
        examples: ['Vexel', 'Zetta', 'Novex', 'Lumina', 'Kovra', 'Synthex']
      }
    ],
    curatedCategories: [
      {
        archetype: 'B2B SaaS, Cloud & Developer Tool Names',
        description: 'Clean, developer-friendly names for software platforms, APIs, and cloud infrastructure.',
        names: ['VertexLabs', 'CloudPulse', 'LogicStack', 'SyncMatrix', 'OmniGrid', 'VeloCode', 'DataForge', 'ArcSystem', 'NovaDeploy', 'KinetixHQ', 'Shiftware', 'CoreMetrics']
      },
      {
        archetype: 'Fintech, Cybersecurity & Enterprise Tech Names',
        description: 'High-trust names for financial technology, security, and enterprise software.',
        names: ['AegisVault', 'SovereignPay', 'Veritas Cyber', 'IronLedger', 'BastionSec', 'Meridian Capital Tech', 'Vanguard Cloud', 'CipherCore', 'Pinnacle FinTech', 'SentinelOS', 'TrueEquity Tech', 'Zenith Shield']
      }
    ],
    auditCriteria: [
      { dimension: 'Podcast & Sales Call Radio Test', detail: 'In B2B SaaS, SDRs and founders say the company name dozens of times a day on Zoom calls—if prospects have to ask "How do you spell that?", you lose pipeline.' },
      { dimension: 'Class 09 & Class 42 Clearance', detail: 'Downloadable software (Class 09) and SaaS (Class 42) are heavily contested; compound or coined names minimize legal friction.' }
    ],
    faqs: [
      {
        question: 'What makes a B2B SaaS startup name memorable?',
        answer: 'The best SaaS names (Stripe, Slack, Notion, Rippling, Ramp) use short, concrete metaphors of movement, structure, or clarity rather than literal acronyms.'
      },
      {
        question: 'What if the exact single-word .com is taken for my tech startup?',
        answer: 'Top YC and enterprise startups append clean modifiers like "HQ.com", "Labs.com", "App.com", or "Cloud.com" until they raise Series A/B to acquire the exact-match domain.'
      }
    ]
  },

  // ==========================================
  // 24. REAL ESTATE BRAND NAME GENERATOR
  // ==========================================
  {
    key: 'realEstateBrand',
    legacyKeys: ['real-estate-brand-name-generator'],
    navLabel: 'Real Estate Brand Names',
    category: 'Tech, Auto & Commerce',
    targetKeyword: 'real estate brand name generator',
    slug: '/real-estate-brand-name-generator/',
    h1: 'Real Estate Brand Name Generator',
    subtitle: 'Build prestigious, high-trust real estate brand names for luxury brokerages, commercial property development firms, REITs, PropTech startups, and architectural estates.',
    defaultIndustryInput: 'Real Estate',
    defaultKeywordsInput: 'crest, meridian, estate, haven',
    defaultStyle: 'Classic',
    seoTitle: 'Real Estate Brand Name Generator: 500+ Brokerage & Property Ideas | BrandGenius',
    seoDescription: 'Generate authoritative real estate brand names for brokerages, property developers, luxury estates, and PropTech companies. Free domain check.',
    phoneticInsight: 'Real estate is the largest financial transaction in most people’s lives. Names built on architectural permanence (Crest, Stone, Meridian, Pillar, Sovereign, Haven) and balanced serif typography project stability and fiduciary trust.',
    marketPositioning: 'Stand out from sea-of-sameness legacy franchises by naming your real estate group, development firm, or PropTech platform like a private wealth institution or modern architectural studio.',
    frameworks: [
      {
        name: '01. Luxury Brokerage & Private Client Group Names',
        explanation: 'Projects white-glove discretion, trophy properties, and architectural pedigree.',
        examples: ['Sovereign Estates', 'Meridian Realty Group', 'Vanguard Properties', 'Aurelia Fine Homes', 'Crestline Residential', 'Noble Manor Co.']
      },
      {
        name: '02. Commercial Development, Capital & REIT Names',
        explanation: 'Institutional-grade names for land acquisition, multi-family development, and real estate funds.',
        examples: ['Pinnacle Land & Capital', 'Cornerstone Equity', 'Vertex Development', 'Bastion Real Estate', 'Summit Spire Group', 'Veritas Property Partners']
      },
      {
        name: '03. Modern PropTech & Residential Living Names',
        explanation: 'Warm, contemporary names for co-living spaces, rental platforms, and smart home communities.',
        examples: ['HavenLiving', 'LumaHomes', 'Nest & Co.', 'Canopy Residences', 'Keystone PropTech', 'Solara Living']
      }
    ],
    curatedCategories: [
      {
        archetype: 'Luxury Real Estate Brokerage & Team Names',
        description: 'Prestigious names for residential brokerages, realtors, and private client teams.',
        names: ['Sovereign Crest Realty', 'Maison Estates', 'Sterling & Stone', 'Vanguard Residential', 'Meridian Fine Homes', 'Palazzo Properties', 'Valour Real Estate', 'Crown & Key', 'Echelon Estates', 'Lumina Realty', 'Kensington Living', 'Noble Hearth']
      },
      {
        archetype: 'Commercial Real Estate, Development & PropTech Names',
        description: 'Authoritative names for builders, investment groups, and property technology.',
        names: ['Apex Terra Group', 'Monolith Development', 'Axis Property Ventures', 'Keystone Capital Realty', 'Foundry Estates', 'Horizon Land Co.', 'PrimeStructure', 'Vector PropTech', 'Citadel Real Estate', 'SummitBuilds', 'TrueNorth Properties', 'Gridline Realty']
      }
    ],
    auditCriteria: [
      { dimension: 'Yard Sign & Billboard Distance Legibility', detail: 'Real estate names must be readable from a moving car at 35 mph on a "For Sale" post or construction site banner.' },
      { dimension: 'Institutional Trust', detail: 'Avoid cute puns in real estate—high-net-worth buyers and commercial tenants demand gravitas.' }
    ],
    faqs: [
      {
        question: 'How do I name a real estate team or brokerage without using my last name?',
        answer: 'Use geographic elevation or architectural terms (like Compass, Corcoran, Douglas Elliman, Meridian, Crestline, or Vanguard) that allow you to recruit agents and eventually sell the brokerage as an independent asset.'
      }
    ]
  },

  // ==========================================
  // 25. PET BRAND NAME GENERATOR
  // ==========================================
  {
    key: 'petBrand',
    legacyKeys: ['pet-brand-name-generator'],
    navLabel: 'Pet Brand Names',
    category: 'Fitness, Health & Wellness',
    targetKeyword: 'pet brand name generator',
    slug: '/pet-brand-name-generator/',
    h1: 'Pet Brand Name Generator',
    subtitle: 'Create warm, trustworthy, and playful pet brand names for human-grade dog food startups, veterinary wellness supplements, luxury pet accessories, and cat care lines.',
    defaultIndustryInput: 'Pet',
    defaultKeywordsInput: 'paw, kindred, tail, wholesome',
    defaultStyle: 'Creative',
    seoTitle: 'Pet Brand Name Generator: 500+ Dog, Cat & Pet Care Ideas | BrandGenius',
    seoDescription: 'Generate memorable pet brand names for dog food, pet supplements, grooming brands, and luxury pet accessories. Instant domain check & guide.',
    phoneticInsight: 'The "humanization of pets" has shifted pet naming away from cartoonish puns toward warm, affectionate, human-grade lifestyle names (like Wild One, Ollie, Farmer’s Dog, BARK, and Kinship).',
    marketPositioning: 'Modern pet parents treat their dogs and cats as family members. Whether you sell freeze-dried raw nutrition, orthopedic dog beds, or calming chews, your name must convey unconditional love and veterinary safety.',
    frameworks: [
      {
        name: '01. Human-Grade Nutrition & Vet Wellness Names',
        explanation: 'Wholesome, science-backed names for fresh pet food, probiotics, and hip-and-joint supplements.',
        examples: ['WholePaw Nutrition', 'Kindred Hound', 'VitalTail Labs', 'PureBowl Co.', 'ThrivePet', 'GoodBoy Biome']
      },
      {
        name: '02. Design-Forward Pet Gear & Luxury Accessories',
        explanation: 'Aesthetic, minimalist names for monochrome leashes, harnesses, carriers, and ceramic bowls.',
        examples: ['Studio Hound', 'LumaLeash', 'Maison Paw', 'Rover & Co.', 'Velvet Collar', 'Nomad Hound']
      }
    ],
    curatedCategories: [
      {
        archetype: 'Fresh Pet Food, Treats & Supplement Brand Names',
        description: 'Trustworthy, appetizing names for dog and cat nutrition.',
        names: ['HarvestHound', 'PurePaws Kitchen', 'VitalBark', 'MeadowTail', 'HonestBowl', 'Solara Pet Wellness', 'NectarPaw', 'TrueCompanion Foods', 'BiomeHound', 'HappySnout Co.', 'GoldenFetch', 'TerraPet']
      },
      {
        archetype: 'Modern Pet Gear, Grooming & Boutique Names',
        description: 'Stylish names for leashes, beds, pet spas, and accessories.',
        names: ['WanderHound', 'PlushPaw Studio', 'Velour Pet', 'Canopy & Leash', 'AuraPet Co.', 'NobleHound', 'Bramble & Bark', 'CozyDen Pets', 'Kinetix K9', 'fetchTheory', 'HaloHound', 'UrbanPaws']
      }
    ],
    auditCriteria: [
      { dimension: 'Vet & Pet-Parent Trust', detail: 'For ingested pet products (food/supplements), prioritize transparency and health cues over silly jokes.' }
    ],
    faqs: [
      {
        question: 'What makes a modern DTC pet brand name successful?',
        answer: 'Leading pet brands (Wild One, Fable, Ollie, Native Pet) use human-grade lifestyle naming that looks great in a modern living room rather than loud, cluttered pet-store packaging.'
      }
    ]
  },

  // ==========================================
  // 26. HOME DECOR BRAND NAME GENERATOR
  // ==========================================
  {
    key: 'homeDecorBrand',
    legacyKeys: ['home-decor-brand-name-generator'],
    navLabel: 'Home Decor Brand Names',
    category: 'Luxury, Jewelry & Lifestyle',
    targetKeyword: 'home decor brand name generator',
    slug: '/home-decor-brand-name-generator/',
    h1: 'Home Decor & Furniture Brand Name Generator',
    subtitle: 'Curate architectural, sanctuary-inspired brand names for interior design studios, artisan furniture makers, luxury bedding lines, lighting brands, and home goods.',
    defaultIndustryInput: 'Home Decor',
    defaultKeywordsInput: 'sanctuary,haven, linen, studio',
    defaultStyle: 'Classic',
    seoTitle: 'Home Decor Brand Name Generator: 500+ Interior & Furniture Ideas | BrandGenius',
    seoDescription: 'Generate timeless home decor, furniture, bedding, and interior design brand names. Instant domain availability checks & naming strategy.',
    phoneticInsight: 'Home and interior naming favors grounded, tactile consonants and spacious vowels that evoke natural materials—linen, travertine, oak, ceramic, and warm ambient light.',
    marketPositioning: 'From DTC organic bedding and mid-century modern furniture to boutique interior design practices, a home brand name must feel calm, curated, and enduring.',
    frameworks: [
      {
        name: '01. Architectural & Interior Design Studio Names',
        explanation: 'Refined names for bespoke furniture, lighting, and residential design studios.',
        examples: ['Atelier Haven', 'Maison Terra', 'Studio Meridian', 'Forma Interiors', 'Aurelia Home', 'Vanguard Living']
      },
      {
        name: '02. Tactile Bedding, Linen & Sanctuary Home Names',
        explanation: 'Soft, restorative names for sheets, towels, rugs, and ceramics.',
        examples: ['LinenSanctuary', 'CloudWeave Home', 'QuietHearth', 'Solstice Living', 'PureVessel Home', 'Willow & Loom']
      }
    ],
    curatedCategories: [
      {
        archetype: 'Luxury Furniture, Lighting & Interior Studio Names',
        description: 'Sophisticated names for timeless furniture, lighting, and decor brands.',
        names: ['Maison Solis', 'Atelier Oak', 'Sovereign Living', 'Lumina Home', 'Veritas Interiors', 'Palazzo Decor', 'Aether Living', 'NobleHearth', 'Monument Home', 'Crestline Studio', 'Valour Furnishings', 'Arbor & Stone']
      },
      {
        archetype: 'Cozy Bedding, Ceramics & Lifestyle Home Names',
        description: 'Warm, organic names for textiles, tableware, and modern home accents.',
        names: ['Haven & Loom', 'TerraCeramic', 'Sunlit Sanctuary', 'LinenTheory Home', 'Meadow & Clay', 'Solace Living', 'Kindred Abode', 'Dune & Dwell', 'Canopy Home', 'Hearthstone Goods', 'PureNest Co.', 'Velvet Cushion']
      }
    ],
    auditCriteria: [
      { dimension: 'Aesthetic Longevity', detail: 'Furniture and home decor are multi-year investments—avoid fleeting slang so your brand feels timeless in a client’s home.' }
    ],
    faqs: [
      {
        question: 'How do I name a home decor or furniture brand?',
        answer: 'Draw inspiration from natural materials, light, and architectural calm (like Parachute, Brooklinen, Lulu and Georgia, Arhaus, or Haven & Loom).'
      }
    ]
  },

  // ==========================================
  // 27. ECOMMERCE BRAND NAME GENERATOR
  // ==========================================
  {
    key: 'ecommerceBrand',
    legacyKeys: ['ecommerce-brand-name-generator'],
    navLabel: 'E-Commerce Brand Names',
    category: 'Tech, Auto & Commerce',
    targetKeyword: 'ecommerce brand name generator',
    slug: '/ecommerce-brand-name-generator/',
    h1: 'E-Commerce & DTC Store Name Generator',
    subtitle: 'Launch your online store with high-converting, memorable e-commerce brand names engineered for Shopify stores, Amazon FBA brands, and curated marketplaces.',
    defaultIndustryInput: 'Ecommerce',
    defaultKeywordsInput: 'direct, curated, goods, prime',
    defaultStyle: 'Modern',
    seoTitle: 'E-Commerce Brand Name Generator: 500+ Online Store & DTC Ideas | BrandGenius',
    seoDescription: 'Generate catchy, high-converting e-commerce store names for Shopify, Amazon FBA, and DTC brands. Instant .com domain availability checks.',
    phoneticInsight: 'In e-commerce, direct-type-in traffic and return ROAS depend on zero-friction spelling. Short compound words and rhythmic coined names prevent customers from getting lost on Google or Amazon search.',
    marketPositioning: 'Whether you are building a single-hero-product DTC brand on Shopify or a multi-SKU Amazon FBA storefront, your e-commerce brand name must build instant checkout trust.',
    frameworks: [
      {
        name: '01. Category-King DTC Brand Names',
        explanation: 'Transforms an everyday consumer product into an aspirational lifestyle brand.',
        examples: ['LumaGoods', 'PrimeVessel', 'VeloDirect', 'AuraCurated', 'TrueStandard Co.', 'ApexSupply']
      },
      {
        name: '02. Multi-Category Boutique & Marketplace Names',
        explanation: 'Elastic umbrella names that allow you to test and scale multiple product verticals.',
        examples: ['HavenMarket', 'Solara Goods', 'Meridian Collective', 'Kindred Supply', 'NovaCurate', 'Vanguard Store']
      }
    ],
    curatedCategories: [
      {
        archetype: 'Modern Shopify DTC & Consumer Brand Names',
        description: 'Clean, memorable names built for Meta/TikTok ads and high conversion rates.',
        names: ['LumaSupply', 'VeloGoods', 'PureStandard', 'AuraDirect', 'Kinetix Co.', 'NovaVessel', 'ShiftGoods', 'TrueHaven', 'OptiLife Store', 'Zenith Supply', 'PulseGoods', 'CoreCurated']
      },
      {
        archetype: 'Amazon FBA & Private Label Brand Names',
        description: 'Trademark-ready names designed for Amazon Brand Registry approval.',
        names: ['ValourPro', 'ApexHome Goods', 'Veritas Supply', 'Sovereign Craft', 'TitanStandard', 'Pinnacle Direct', 'Vanguard Essentials', 'Meridian Goods', 'PrimeCrest', 'Ironclad Supply', 'Solis Goods', 'NoblePath']
      }
    ],
    auditCriteria: [
      { dimension: 'Amazon Brand Registry & USPTO Readiness', detail: 'Amazon Brand Registry requires an active or pending trademark—avoid generic descriptive strings and use distinctive compound or coined names.' }
    ],
    faqs: [
      {
        question: 'What is the best name strategy for an Amazon FBA or Shopify store?',
        answer: 'Choose a brandable umbrella name (like Anker, Brooklinen, or Monos) that sounds like a real company rather than a keyword-stuffed phrase, ensuring fast Amazon Brand Registry approval and high customer trust.'
      }
    ]
  },

  // ==========================================
  // 28. GAMING BRAND NAME GENERATOR
  // ==========================================
  {
    key: 'gamingBrand',
    legacyKeys: ['gaming-brand-name-generator'],
    navLabel: 'Gaming & Esports Names',
    category: 'Tech, Auto & Commerce',
    targetKeyword: 'gaming brand name generator',
    slug: '/gaming-brand-name-generator/',
    h1: 'Gaming & Esports Brand Name Generator',
    subtitle: 'Forge high-FPS, iconic gaming brand names for indie game studios, esports organizations, gaming peripherals, mechanical keyboards, and creator collectives.',
    defaultIndustryInput: 'Gaming',
    defaultKeywordsInput: 'nexus, vortex, pixel, arena',
    defaultStyle: 'Modern',
    seoTitle: 'Gaming Brand Name Generator: 500+ Esports & Game Studio Ideas | BrandGenius',
    seoDescription: 'Generate legendary gaming brand names for game studios, esports teams, hardware brands, and streamers. Instant domain checks & naming guide.',
    phoneticInsight: 'Gaming and hardware nomenclature thrives on sharp, high-contrast phonetics (Razer, Fnatic, Sentinels, Riot, Valve, HyperX)—leveraging X, V, Z, K, and N sounds that project speed, precision, and mythic scale.',
    marketPositioning: 'Whether you are shipping an indie Steam title, launching an ultralight wireless gaming mouse, or building a championship esports roster, your name must look iconic in RGB LED lighting and Twitch overlays.',
    frameworks: [
      {
        name: '01. Esports Org & Competitive Syndicate Names',
        explanation: 'Commanding, monolithic names built for jerseys, chants, and tournament broadcasts.',
        examples: ['Vanguard Esports', 'Sovereign Gaming', 'Phantom Syndicate', 'Apex Legion', 'Obsidian Clan', 'Valour Guild']
      },
      {
        name: '02. Indie Game Studio & Interactive World Names',
        explanation: 'Imaginative, evocative names for game developers and interactive entertainment.',
        examples: ['Nebula Forge Studios', 'Aether Interactive', 'ChronoPixel Games', 'Mythic Engine', 'Starlight Realm', 'Lumina Interactive']
      },
      {
        name: '03. High-Performance Gaming Hardware & Peripherals',
        explanation: 'Precision technical names for mechanical keyboards, mice, headsets, and PC builds.',
        examples: ['Kinetix Gear', 'VoltPeripherals', 'HyperSwitch', 'Vector Mouse Co.', 'PulseAudio Gaming', 'ZeroLatency Lab']
      }
    ],
    curatedCategories: [
      {
        archetype: 'Game Development Studio & Publisher Names',
        description: 'Memorable studio names for indie developers, AA/AAA teams, and VR creators.',
        names: ['Arcane Forge Games', 'Solstice Interactive', 'Quantum Realm Studios', 'Obsidian Pixel', 'Vortex Works', 'Aetherion Games', 'Lumen Quest', 'Horizon Interactive', 'Mythos Lab', 'NovaSphere Games', 'Ironclad Studios', 'Zenith Interactive']
      },
      {
        archetype: 'Esports Team & Gaming Hardware Brand Names',
        description: 'Sharp, aggressive names for competitive rosters and gaming gear.',
        names: ['Kryos Gaming', 'Vexel Esports', 'Stryke Peripherals', 'ApexPulse', 'ShadowCore', 'TitanFrame', 'VelocityGG', 'PhantomSwitch', 'OmniArena', 'ShiftGaming', 'Valex Esports', 'NoirGear']
      }
    ],
    auditCriteria: [
      { dimension: '3-Letter Esports Tag Compatibility', detail: 'Competitive gaming brands often abbreviate to a 3-or-4 letter clan tag (e.g., SEN, FNC, G2, NRG)—choose a name with strong initials.' }
    ],
    faqs: [
      {
        question: 'How do I name an indie game studio or esports brand?',
        answer: 'Pair a mythic or cosmic noun (Aether, Obsidian, Arcane, Nebula, Vanguard) with a craft or tech word (Forge, Interactive, Works, Syndicate), or coin a sharp 1-word handle.'
      }
    ]
  },

  // ==========================================
  // 29. BABY BRAND NAME GENERATOR
  // ==========================================
  {
    key: 'babyBrand',
    legacyKeys: ['baby-brand-name-generator'],
    navLabel: 'Baby & Kids Brand Names',
    category: 'Fashion & Apparel',
    targetKeyword: 'baby brand name generator',
    slug: '/baby-brand-name-generator/',
    h1: 'Baby & Kids Brand Name Generator',
    subtitle: 'Create gentle, parent-trusted baby brand names for organic bamboo onesies, non-toxic nursery gear, gentle baby skincare, and heirloom wooden toys.',
    defaultIndustryInput: 'Baby',
    defaultKeywordsInput: 'nursery, gentle, cloud, sprout',
    defaultStyle: 'Creative',
    seoTitle: 'Baby Brand Name Generator: 500+ Cute & Trusted Baby Brand Ideas | BrandGenius',
    seoDescription: 'Generate gentle, memorable baby and kids brand names for baby clothing, nursery gear, toys, and skincare. Instant domain availability checks.',
    phoneticInsight: 'Baby and parenting nomenclature relies on bilabial stops and nasals (M, B, P, L, N) paired with soft vowels—the exact phonetic building blocks of early infant speech—creating an immediate subconscious sense of safety, softness, and warmth.',
    marketPositioning: 'Millennial and Gen-Z parents research safety certifications, organic cotton weaves, and non-toxic materials obsessively. Your baby brand name must feel tender and imaginative while projecting uncompromising pediatric safety.',
    frameworks: [
      {
        name: '01. Organic Bamboo & Heirloom Baby Apparel Names',
        explanation: 'Soft, cozy names for swaddles, sleep sacks, and organic cotton playwear.',
        examples: ['CloudNest Baby', 'Linen & Lullaby', 'LittleSprout Co.', 'Meadow & Moon', 'Velvet Cub', 'featherSoft Baby']
      },
      {
        name: '02. Modern Nursery Gear, Strollers & Educational Toys',
        explanation: 'Scandinavian-inspired, design-forward names for strollers, cribs, and Montessori toys.',
        examples: ['LumaTot', 'Kindred Nursery', 'Solara Kids', 'PureHaven Baby', 'WonderBlock Studio', 'Bloom & Build']
      }
    ],
    curatedCategories: [
      {
        archetype: 'Organic Baby Clothing, Swaddles & Skincare Names',
        description: 'Soft, nurturing names for newborn essentials and gentle bath products.',
        names: ['Lullaby Loom', 'CloudBerry Baby', 'GentleSprout', 'Moonbeam Nursery', 'PureNest Baby', 'HoneyCub Co.', 'Willow & Tot', 'PetalSoft Baby', 'Starlight Swaddle', 'CozyKin', 'Bramble Baby', 'SunnyDew Kids']
      },
      {
        archetype: 'Modern Baby Gear, Strollers & Montessori Toy Names',
        description: 'Trusted, design-led names for parenting hardware and imaginative play.',
        names: ['Kinship Baby', 'Nook & Nest', 'WonderPath Toys', 'HaloNursery', 'VeloStroll', 'TrueNorth Baby', 'AuraTot', 'Canopy Kids', 'LittleMeridian', 'BrightSeed Co.', 'harborBaby', 'Zenith Nursery']
      }
    ],
    auditCriteria: [
      { dimension: 'Gift Registry & Baby Shower Appeal', detail: 'Baby products are heavily gifted—names that sound elevated and heartwarming perform best on Babylist and Target registries.' }
    ],
    faqs: [
      {
        question: 'What makes parents trust a new baby brand name?',
        answer: 'Parents gravitate toward names that combine nature, softness, and protection (like Kyte Baby, Lovevery, Babylist, Coterie, and Honest) without sounding overly infantile.'
      }
    ]
  },

  // ==========================================
  // 30. SUSTAINABLE & ECO BRAND NAME GENERATOR
  // ==========================================
  {
    key: 'ecoBrand',
    legacyKeys: ['eco-brand-name-generator'],
    navLabel: 'Sustainable & Eco Names',
    category: 'Fitness, Health & Wellness',
    targetKeyword: 'eco brand name generator',
    slug: '/eco-brand-name-generator/',
    h1: 'Sustainable & Eco Brand Name Generator',
    subtitle: 'Generate regenerative, earth-first brand names for zero-waste home products, clean-tech startups, circular fashion labels, and carbon-neutral consumer brands.',
    defaultIndustryInput: 'Eco',
    defaultKeywordsInput: 'terra, verdant, canopy, cycle',
    defaultStyle: 'Modern',
    seoTitle: 'Eco Brand Name Generator: 500+ Sustainable & Green Brand Ideas | BrandGenius',
    seoDescription: 'Create authentic sustainable, eco-friendly, and zero-waste brand names without greenwashing clichés. Free domain checks & naming strategy.',
    phoneticInsight: 'Modern sustainability naming has evolved beyond tired 1990s "Eco-" or "Green-" prefixes. Today’s category leaders (Patagonia, Allbirds, Blueland, Grove, Rivian) use evocative ecological, geological, and cyclical roots (Terra, Canopy, Biome, Solstice, Cycle, Verdant).',
    marketPositioning: 'Conscious consumers have a sharp radar for greenwashing. A great sustainable brand name communicates design excellence and planetary stewardship through poetic natural imagery rather than preachy buzzwords.',
    frameworks: [
      {
        name: '01. Ecological, Botanical & Geological Names',
        explanation: 'Celebrates ecosystems, soil health, oceans, and natural phenomena.',
        examples: ['TerraBiome', 'Verdant Canopy', 'Solstice Earth', 'Meridian Reef', 'Arbor & Tide', 'LivingRoot Co.']
      },
      {
        name: '02. Circular Economy, Refill & Zero-Waste Names',
        explanation: 'Clean, modern names for refillable home care, compostable packaging, and circular goods.',
        examples: ['CycleVessel', 'ZeroLoop', 'PureRefill', 'LumaEarth', 'RenewStandard', 'ClearFootprint']
      }
    ],
    curatedCategories: [
      {
        archetype: 'Zero-Waste Home, Refill & Sustainable DTC Names',
        description: 'Fresh, design-forward names for eco-friendly household and personal care brands.',
        names: ['BlueCanopy', 'TerraLoop', 'Verdant Vessel', 'PureBiome Co.', 'Solara Earth', 'CycleTheory', 'Rooted Standard', 'ClearTide Goods', 'EdenRefill', 'Meadow & Mist', 'KindEarth Lab', 'Horizon Zero']
      },
      {
        archetype: 'CleanTech, Renewable Energy & Climate Startup Names',
        description: 'Authoritative names for solar, carbon capture, EV charging, and green technology.',
        names: ['HelioGrid', 'TerraVolt', 'Aether Renewables', 'ApexSolaris', 'BiomeCarbon', 'Vanguard CleanTech', 'Kinetix Earth', 'Zenith Power', 'NovaCanopy', 'OptiClimate', 'Meridian Green', 'Solstice Energy']
      }
    ],
    auditCriteria: [
      { dimension: 'Anti-Greenwashing Authenticity', detail: 'FTC Green Guides scrutinize unqualified environmental claims—using evocative nature roots (Terra, Canopy, Solstice) is legally safer and more memorable than "100% EcoFriendly".' }
    ],
    faqs: [
      {
        question: 'How do I name an eco-friendly brand without sounding cliché?',
        answer: 'Skip overused prefixes like "Eco-" or "Green-" and instead use specific geological, botanical, or cyclical words like Terra, Canopy, Biome, Tide, Solstice, Vessel, or Loop.'
      }
    ]
  }
];

function buildIndustryPageHtml(cfg: IndustryLandingConfig): string {
  const frameworksHtml = cfg.frameworks
    .map(
      (f) => `
      <div class="p-6 bg-white rounded-xl border border-gray-200 shadow-sm mb-4">
        <h3 class="text-xl font-bold text-gray-900 mb-2">${f.name}</h3>
        <p class="text-gray-600 mb-3">${f.explanation}</p>
        <div class="flex flex-wrap gap-2">
          ${f.examples
            .map(
              (ex) =>
                `<span class="inline-block px-3 py-1 bg-indigo-50 text-indigo-800 font-semibold text-sm rounded-md">${ex}</span>`
            )
            .join('')}
        </div>
      </div>`
    )
    .join('');

  const curatedHtml = cfg.curatedCategories
    .map(
      (cat) => `
      <div class="p-6 bg-gray-50 rounded-xl border border-gray-200 mb-6">
        <h3 class="text-xl font-bold text-gray-900 mb-1">${cat.archetype}</h3>
        <p class="text-sm text-gray-600 mb-4">${cat.description}</p>
        <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2.5">
          ${cat.names
            .map(
              (n) =>
                `<div class="p-2.5 bg-white rounded-lg border border-gray-200 text-gray-800 font-medium text-sm text-center shadow-sm">${n}</div>`
            )
            .join('')}
        </div>
      </div>`
    )
    .join('');

  const auditHtml = cfg.auditCriteria
    .map(
      (a) => `
      <li class="mb-3">
        <strong class="text-gray-900">${a.dimension}:</strong> <span class="text-gray-600">${a.detail}</span>
      </li>`
    )
    .join('');

  const faqsHtml = cfg.faqs
    .map(
      (faq) => `
      <div class="p-5 bg-white rounded-xl border border-gray-200 mb-4">
        <h3 class="text-lg font-bold text-gray-900 mb-2">${faq.question}</h3>
        <p class="text-gray-600 leading-relaxed">${faq.answer}</p>
      </div>`
    )
    .join('');

  return `
    <div class="space-y-12 text-left mt-8">
      <section>
        <h2 class="text-2xl md:text-3xl font-extrabold text-gray-900 mb-4">How to Choose a Category-Winning Name with Our ${cfg.h1}</h2>
        <p class="text-gray-600 leading-relaxed mb-4">${cfg.marketPositioning}</p>
        <p class="text-gray-600 leading-relaxed">${cfg.phoneticInsight}</p>
      </section>

      <section>
        <h2 class="text-2xl md:text-3xl font-extrabold text-gray-900 mb-4">Proven Naming Frameworks for ${cfg.navLabel.replace(' Names', '')}</h2>
        <p class="text-gray-600 mb-6">Professional naming agencies use structured linguistic frameworks rather than random guesswork. Here are the core naming architectures tailored for this vertical:</p>
        ${frameworksHtml}
      </section>

      <section>
        <h2 class="text-2xl md:text-3xl font-extrabold text-gray-900 mb-4">Curated ${cfg.h1.replace(' Generator', '')} Ideas by Brand Archetype</h2>
        <p class="text-gray-600 mb-6">Use these hand-crafted brand name concepts as immediate inspiration or plug their root keywords into the generator above to synthesize hundreds of custom variations:</p>
        ${curatedHtml}
      </section>

      <section class="p-6 md:p-8 bg-indigo-50/60 rounded-2xl border border-indigo-100">
        <h2 class="text-2xl font-extrabold text-indigo-950 mb-4">Consultant Checklist: Auditing Your ${cfg.navLabel.replace(' Names', '')} Brand Name</h2>
        <p class="text-gray-700 mb-4">Before filing your trademark or purchasing packaging inventory, evaluate your shortlist against these deterministic brand audit benchmarks:</p>
        <ul class="list-disc pl-5 space-y-2">
          ${auditHtml}
        </ul>
      </section>

      <section>
        <h2 class="text-2xl md:text-3xl font-extrabold text-gray-900 mb-6">Frequently Asked Questions (${cfg.h1})</h2>
        ${faqsHtml}
      </section>
    </div>
  `;
}

function buildFaqSchemaHead(cfg: IndustryLandingConfig): string {
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: cfg.faqs.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: f.answer
      }
    }))
  };
  return `<script type="application/ld+json">${JSON.stringify(faqSchema)}</script>`;
}

export const industryPagesContentMap: Record<string, PageContent> = {};
export const slugToIndustryConfigMap: Record<string, IndustryLandingConfig> = {};
export const keyToIndustryConfigMap: Record<string, IndustryLandingConfig> = {};

INDUSTRY_LANDING_CONFIGS.forEach((cfg) => {
  const pageData: PageContent = {
    h1: cfg.h1,
    body: buildIndustryPageHtml(cfg),
    seo: {
      title: cfg.seoTitle,
      description: cfg.seoDescription,
      canonical: `https://www.brandgenius.app${cfg.slug}`,
      slug: cfg.slug,
      noindex: false,
      customHead: buildFaqSchemaHead(cfg),
      ogImageUrl: '/og-default.jpg'
    }
  };

  industryPagesContentMap[cfg.key] = pageData;
  keyToIndustryConfigMap[cfg.key] = cfg;
  slugToIndustryConfigMap[cfg.slug.replace(/\/$/, '')] = cfg;
  slugToIndustryConfigMap[cfg.slug] = cfg;

  if (cfg.legacyKeys) {
    cfg.legacyKeys.forEach((lk) => {
      industryPagesContentMap[lk] = pageData;
      keyToIndustryConfigMap[lk] = cfg;
    });
  }
});

export const INDUSTRY_NAV_CHILDREN = INDUSTRY_LANDING_CONFIGS.map((cfg) => ({
  id: cfg.key,
  name: cfg.navLabel,
  path: cfg.slug,
  target: '_self' as const
}));
